import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Crosshair, MapPin, Users, Building2, Swords,
  DollarSign, Star, Shield, Flame, Award, ChevronRight,
  Lock, Zap, TrendingUp, BookOpen, CircleDot, Clock,
  Battery, Target, Crown, ChevronDown, ChevronUp, UserPlus,
  UserMinus, ArrowUpRight, Timer, Gauge, Trophy, ShoppingBag, X,
  PackageOpen, Globe, Check, Dice5, Gamepad2, Send
} from 'lucide-react';
import BackpackMattStore from './BackpackMattStore';
import MattStoreFull from './MattStoreFull';
import GameMusic from './GameMusic';
import JobActionAnimation from './JobActionAnimation';
import InteractiveMission from './InteractiveMission';
import PoliceJobsTab from './PoliceJobsTab';
import GameModesTab from './GameModesTab';
import StoryModeMap from './StoryModeMap';
import GamblingTab from './GamblingTab';
import RunTheBlockPremium from './RunTheBlockPremium';
import CrewLoadout from './CrewLoadout';
import FlightsTab from './FlightsTab';
import ERA_TRANSITION_IMAGES from '../data/eraTransitionImages';
import CREW_PORTRAITS from '../data/crewPortraits';
import PoliceTrapPopup from './PoliceTrapPopup';
import PoliceRaidPopup from './PoliceRaidPopup';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// ─── Character Select Screen ───
// Character photo URLs
const CHAR_PHOTOS = {
  anton: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/ia3znv31_5CD90DB8-C183-4A0C-9DA3-2FD9E7017932.png',
  diego: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/a31grl3d_97F38930-5588-41EC-8500-A9A526D849A2.png',
  vincent: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/sjo3u2tp_D1F96B70-1EFD-49BD-A185-6D6D5B878E99.png',
  francisco: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/qt77lxyv_B47828E9-7D91-4606-901D-C91A192108D5.png',
  antwon: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/tt9nad8e_EEBAFA90-D1BF-4F15-8F84-CC9ED51205CB.png',
  filomena: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/9yst7d1k_D12DBCEE-F26D-41E6-AFB6-7E0D17125B53.png',
  santo: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/egeyfgma_BCC0332F-ED57-4438-957A-B139C1F6A695.png',
  fuego: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/3fcg02zn_9687E48E-7A5C-4A26-A476-75E8A63A644B.png',
  la_sombra: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/2qzmst4d_FF80047C-D255-440B-A64A-A4B92DA38980.png',
  rodriguez: 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/1rdsrjox_055B5680-9953-419A-9817-53E0E4796821.png',
};

const CHARACTER_DATA = {
  cmd: [
    { path: 'anton', img: CHAR_PHOTOS.anton, name: 'ANTON "THE REAPER"', color: '#8B1A1A', label: 'PATH A',
      desc: 'East New York, 1970. Build CMD. Survive the betrayal. Return in 2026.',
      stats: 'Street Smart: 8 / Muscle: 6 / Connections: 3', cash: '$200', crew: 'Antwon + Young Vincent',
      tag: 'AGGRESSIVE. HIGH RISK, HIGH REWARD.' },
    { path: 'vincent', img: CHAR_PHOTOS.vincent, name: 'VINCENT "THE CONSIGLIERE"', color: '#4A7AB5', label: 'PATH B',
      desc: 'Same world. Different eyes. Go legitimate. Survive Anton\'s return.',
      stats: 'Street Smart: 10 / Muscle: 4 / Connections: 6', cash: '$500', crew: 'Antwon',
      tag: 'STRATEGIC. SLOWER BUILD. LONG-TERM EMPIRE.' },
    { path: 'diego', img: CHAR_PHOTOS.diego, name: 'DIEGO "THE GHOST"', color: '#E8E0D0', label: 'PATH C',
      desc: 'Start at Spofford. Neutral operator. Built for survival.',
      stats: 'Street Smart: 7 / Muscle: 5 / Charm: 10', cash: '$300', crew: 'Solo — starts at Era 2',
      tag: 'NEUTRAL. SURVIVAL. PROFIT.' },
  ],
  morelli: [
    { path: 'francisco', img: CHAR_PHOTOS.francisco, name: 'FRANCISCO "THE OLD REAPER"', color: '#9CA3AF', label: 'THE PATRIARCH',
      desc: 'Italy to New York, 1952. Build the Morelli empire from the old country. Fear is the foundation.',
      stats: 'Street Smart: 6 / Muscle: 8 / Connections: 5', cash: '$1,000', crew: 'Sal + Carmine',
      tag: 'OLD WORLD POWER. GENERATIONAL LEGACY.' },
    { path: 'antwon', img: CHAR_PHOTOS.antwon, name: 'ANTWON "THE BULL"', color: '#C9A84C', label: 'THE FOUNDATION',
      desc: 'Kingston to Brooklyn. The muscle who raised Anton. Build the foundation the empire stands on.',
      stats: 'Street Smart: 7 / Muscle: 10 / Connections: 4', cash: '$150', crew: 'Young Anton',
      tag: 'PURE STRENGTH. LOYALTY ABOVE ALL.' },
    { path: 'filomena', img: CHAR_PHOTOS.filomena, name: 'FILOMENA MORELLI', color: '#9F7AEA', label: 'THE SHADOW',
      desc: 'A Morelli by blood, a ghost by choice. The woman operating where no one looks. The real power.',
      stats: 'Street Smart: 10 / Muscle: 3 / Connections: 8', cash: '$800', crew: 'Solo — intelligence network',
      tag: 'INVISIBLE. PATIENT. THE REAL POWER.' },
  ],
  ddn: [
    { path: 'santo', img: CHAR_PHOTOS.santo, name: 'SANTO "THE ARCHITECT"', color: '#FF6B35', label: 'THE FOUNDER',
      desc: 'Santo Domingo to Washington Heights. Built the DDN pipeline from a bodega phone and a dream.',
      stats: 'Street Smart: 8 / Muscle: 5 / Connections: 7', cash: '$600', crew: 'Diaz + Machete',
      tag: 'THE PIPELINE BUILDER. VISION OVER VIOLENCE.' },
    { path: 'fuego', img: CHAR_PHOTOS.fuego, name: 'FUEGO', color: '#EF4444', label: 'THE ENFORCER',
      desc: 'Young, reckless, ready to die for the block. Brooklyn is calling — CMD doesn\'t know what\'s coming.',
      stats: 'Street Smart: 6 / Muscle: 9 / Connections: 3', cash: '$200', crew: 'Solo — earns crew through fire',
      tag: 'AGGRESSIVE. EXPANSION. WAR.' },
    { path: 'la_sombra', img: CHAR_PHOTOS.la_sombra, name: 'LA SOMBRA', color: '#6B7280', label: 'THE INTELLIGENCE',
      desc: 'Nobody sees La Sombra. The DDN\'s eyes and ears. She knows every secret before it happens.',
      stats: 'Street Smart: 10 / Muscle: 4 / Connections: 6', cash: '$400', crew: 'Solo — builds a spy network',
      tag: 'INVISIBLE. INFORMATION IS POWER.' },
  ],
  badge: [
    { path: 'rodriguez', img: CHAR_PHOTOS.rodriguez, name: 'COMMISSIONER TORIBIO RODRIGUEZ', color: '#1E90FF', label: 'THE COMMISSIONER',
      desc: 'Trinidadian and Ecuadorian heritage. Based in New York City. Rose through the ranks as the dirtiest cop in the department. Founder of Operation Low Tide — the Interstate Organized Crime Task Force. His mission: take down CMD, Morelli, and DDN. But every badge has a price.',
      stats: 'Intelligence: 9 / Authority: 10 / Tactical: 6 / Connections: 10', cash: '$10,000', crew: 'NYPD Task Force',
      tag: 'THE COMMANDER. WARRANTS. RAIDS. PRESSURE. CONTROL.' },
  ],
};

const CharacterCard = ({ c, isSelected, hasImage, onClick }) => (
  <button onClick={onClick}
    className={`group bg-[#111820] border-2 rounded-lg overflow-hidden text-left transition-all duration-300 ${
      isSelected ? 'ring-2 scale-[1.03] shadow-lg shadow-black/50' : 'hover:scale-[1.01]'
    }`}
    style={{
      borderColor: isSelected ? c.color : c.color + '40',
      ringColor: c.color,
    }}
    data-testid={`select-${c.path}`}>
    {hasImage ? (
      <div className="relative h-56 overflow-hidden">
        <img src={c.img} alt={c.name} className={`w-full h-full object-cover transition-all duration-500 ${
          isSelected ? 'grayscale-0 scale-105' : 'grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105'
        }`} style={{ objectPosition: c.path === 'rodriguez' ? 'center top' : '50% 0%' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111820] via-transparent to-transparent" />
        <div className="absolute top-2 left-2 flex items-center gap-2">
          <CircleDot size={14} style={{ color: c.color }} />
          <span className="font-['Outfit'] text-xs tracking-widest" style={{ color: c.color }}>{c.label}</span>
        </div>
        {isSelected && (
          <div className="absolute top-2 right-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: c.color }}>
              <Check size={14} className="text-[#050505]" />
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className="relative h-48 bg-[#0a0e12] flex items-center justify-center border-b border-[#1A2535]">
        <div className="text-center">
          <span className="font-['Cormorant_Garamond'] text-3xl font-bold" style={{ color: isSelected ? c.color : c.color + '30' }}>{c.name.charAt(0)}</span>
        </div>
        <div className="absolute top-2 left-2 flex items-center gap-2">
          <CircleDot size={14} style={{ color: c.color }} />
          <span className="font-['Outfit'] text-xs tracking-widest" style={{ color: c.color }}>{c.label}</span>
        </div>
        {isSelected && (
          <div className="absolute top-2 right-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: c.color }}>
              <Check size={14} className="text-[#050505]" />
            </div>
          </div>
        )}
      </div>
    )}
    <div className="p-4">
      <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0] mb-1">{c.name}</h3>
      <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs mb-3">{c.desc}</p>
      <div className="space-y-1 text-[11px] font-['Outfit'] text-[#E8E0D0]/40">
        <p>{c.stats.split(' / ').map((s, i) => <span key={i}>{i > 0 && ' / '}{s.split(': ')[0]}: <span className="text-[#C9A84C]">{s.split(': ')[1]}</span></span>)}</p>
        <p>Starting Cash: <span className="text-[#C9A84C]">{c.cash}</span></p>
        <p>Crew: {c.crew}</p>
      </div>
      <p className={`font-['Outfit'] text-xs mt-3 tracking-wider transition-colors ${
        isSelected ? 'text-[#E8E0D0]' : 'group-hover:text-[#E8E0D0]'
      }`} style={{ color: isSelected ? undefined : c.color }}>{c.tag}</p>
    </div>
  </button>
);

const CharacterSelect = ({ onStart, onResume }) => {
  const [username, setUsername] = useState(localStorage.getItem('kow_username') || '');
  const [email, setEmail] = useState(localStorage.getItem('kow_email') || '');
  const [faction, setFaction] = useState(localStorage.getItem('kow_faction') || 'cmd');
  const [selectedPath, setSelectedPath] = useState(null);
  const [resumeMode, setResumeMode] = useState(false);
  const [resumeEmail, setResumeEmail] = useState('');
  const [savedGames, setSavedGames] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);

  const factions = [
    { id: 'cmd', name: 'CMD', color: '#C9A84C', desc: 'Anton\'s empire. Street kings.' },
    { id: 'morelli', name: 'MORELLI FAMILY', color: '#8B1A1A', desc: 'Old-world power. Blood and legacy.' },
    { id: 'ddn', name: 'DDN', color: '#4A7AB5', desc: 'Dominican Distribution Network.' },
    { id: 'badge', name: 'THE BADGE', color: '#1E90FF', desc: 'Operation Low Tide. The law.' },
  ];

  const handleConfirmStart = () => {
    if (!selectedPath) return;
    const name = username.trim() || 'Anonymous';
    const playerEmail = email.trim() || null;
    localStorage.setItem('kow_username', name);
    localStorage.setItem('kow_faction', faction);
    if (playerEmail) localStorage.setItem('kow_email', playerEmail);
    onStart(selectedPath, name, faction, playerEmail);
  };

  const handleResume = async () => {
    const query = resumeEmail.trim();
    if (!query) return;
    setResumeLoading(true);
    setResumeError('');
    setSavedGames(null);
    try {
      const res = await fetch(`${API}/api/game/resume/${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        const games = data.games || data || [];
        if (Array.isArray(games) && games.length > 0) {
          setSavedGames(games);
        } else {
          setResumeError('No games found. Try your email or exact username.');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setResumeError(err.detail || 'No games found. Try your email or exact username.');
      }
    } catch {
      setResumeError('Connection failed. Please try again.');
    }
    setResumeLoading(false);
  };

  const handleLoadGame = (gameId) => {
    localStorage.setItem('kow_game_id', gameId);
    localStorage.setItem('kow_email', resumeEmail.trim());
    onResume(gameId);
  };

  // Reset selection when faction changes
  const handleFactionChange = (fId) => {
    setFaction(fId);
    setSelectedPath(null);
  };

  const characters = CHARACTER_DATA[faction] || [];
  const selectedChar = characters.find(c => c.path === selectedPath);
  const hasCmdImages = faction === 'cmd';

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="film-grain" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl w-full">
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] text-center mb-2">
          KILLING OVER WATER
        </h1>
        <p className="font-['Outfit'] text-[#E8E0D0]/60 text-center text-sm tracking-widest uppercase mb-2">
          {resumeMode ? 'RESUME SAVED GAME' : 'START NEW GAME'}
        </p>
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-center italic mb-8">
          "The water remembers every move you make."
        </p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4 max-w-xl mx-auto">
          <div className="flex-1">
            <label className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest block mb-1">YOUR NAME</label>
            <input
              type="text" value={username} onChange={e => setUsername(e.target.value)}
              placeholder="Anonymous" maxLength={20}
              className="w-full bg-[#111820] border border-[#1A2535] rounded px-3 py-2 font-['Outfit'] text-[#E8E0D0] text-sm placeholder-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none transition-colors"
              data-testid="username-input"
            />
          </div>
          <div className="flex-1">
            <label className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest block mb-1">EMAIL <span className="text-[#E8E0D0]/15">(SAVE PROGRESS)</span></label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-[#111820] border border-[#1A2535] rounded px-3 py-2 font-['Outfit'] text-[#E8E0D0] text-sm placeholder-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none transition-colors"
              data-testid="email-input"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6 max-w-xl mx-auto">
          <div className="flex-1">
            <label className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest block mb-1">PLEDGE FACTION</label>
            <div className="flex gap-1">
              {factions.map(f => (
                <button key={f.id} onClick={() => handleFactionChange(f.id)}
                  className={`flex-1 px-2 py-2 rounded font-['Outfit'] text-[10px] tracking-widest transition-all border ${
                    faction === f.id ? 'border-opacity-60' : 'border-[#1A2535] opacity-40 hover:opacity-70'
                  }`}
                  style={faction === f.id ? { borderColor: f.color, color: f.color, background: f.color + '15' } : {}}
                  title={f.desc} data-testid={`faction-${f.id}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 flex items-end">
            <button onClick={() => { setResumeMode(!resumeMode); setSelectedPath(null); }}
              className="w-full px-3 py-2 bg-[#111820] border border-[#C9A84C]/20 rounded font-['Outfit'] text-[#C9A84C]/60 text-[10px] tracking-widest hover:text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors"
              data-testid="resume-toggle">
              {resumeMode ? 'BACK TO NEW GAME' : 'RESUME SAVED GAME'}
            </button>
          </div>
        </div>

        {resumeMode ? (
          <div className="max-w-xl mx-auto mb-6">
            <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-6">
              <h3 className="font-['Outfit'] text-[#C9A84C] text-xs tracking-widest mb-3">FIND YOUR GAME</h3>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mb-3">Search by email or exact username</p>
              <div className="flex gap-2">
                <input
                  type="text" value={resumeEmail} onChange={e => setResumeEmail(e.target.value)}
                  placeholder="Email or username"
                  className="flex-1 bg-[#0a0e12] border border-[#1A2535] rounded px-3 py-2 font-['Outfit'] text-[#E8E0D0] text-sm placeholder-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none transition-colors"
                  data-testid="resume-email-input"
                  onKeyDown={e => e.key === 'Enter' && handleResume()}
                />
                <button onClick={handleResume} disabled={resumeLoading}
                  className="px-4 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[10px] tracking-widest hover:bg-[#C9A84C]/20 transition-colors disabled:opacity-50"
                  data-testid="resume-search-btn">
                  {resumeLoading ? '...' : 'FIND'}
                </button>
              </div>
              {resumeError && <p className="font-['Outfit'] text-[#8B1A1A] text-xs mt-2" data-testid="resume-error">{resumeError}</p>}
              {savedGames && savedGames.length > 0 && (
                <div className="mt-4 space-y-2">
                  {savedGames.map(g => (
                    <button key={g.game_id} onClick={() => handleLoadGame(g.game_id)}
                      className="w-full bg-[#0a0e12] border border-[#1A2535] hover:border-[#C9A84C]/30 rounded-lg p-3 text-left transition-colors"
                      data-testid={`resume-game-${g.game_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{g.path_name}</span>
                          <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs ml-2">as {g.username}</span>
                        </div>
                        <span className={`font-['Outfit'] text-[10px] tracking-widest ${g.game_complete ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/40'}`}>
                          {g.game_complete ? 'COMPLETE' : `ERA ${g.current_era}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="font-['Outfit'] text-[#C9A84C]/50 text-[11px]">Lv.{g.level} {g.level_title}</span>
                        <span className="font-['Outfit'] text-[#C9A84C]/50 text-[11px]">${g.cash?.toLocaleString()}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
        <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#E8E0D0] text-center mb-2">
          {faction === 'badge' ? 'THE COMMISSIONER' : faction === 'cmd' ? 'CHOOSE YOUR SOLDIER' : faction === 'morelli' ? 'CHOOSE YOUR PATH — ORIGINS' : 'CHOOSE YOUR OPERATIVE'}
        </h2>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-center text-[10px] tracking-widest mb-6">
          <a href="/leaderboard" className="hover:text-[#C9A84C] transition-colors" data-testid="view-leaderboard-link">VIEW THE LEDGER</a>
        </p>

        {/* Character cards — unified for all factions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {characters.map(c => (
            <CharacterCard
              key={c.path}
              c={c}
              isSelected={selectedPath === c.path}
              hasImage={!!c.img}
              onClick={() => setSelectedPath(selectedPath === c.path ? null : c.path)}
            />
          ))}
        </div>

        {/* Confirmation panel */}
        <AnimatePresence>
          {selectedChar && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className="mt-6 mx-auto max-w-lg"
              data-testid="confirm-selection-panel"
            >
              <div className="bg-[#111820] border rounded-lg p-5 text-center"
                style={{ borderColor: selectedChar.color + '60' }}>
                <p className="font-['Outfit'] text-[9px] tracking-[0.3em] text-[#E8E0D0]/30 mb-2">CONFIRM SELECTION</p>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] mb-1">{selectedChar.name}</h3>
                <p className="font-['Outfit'] text-xs mb-4" style={{ color: selectedChar.color }}>{selectedChar.tag}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleConfirmStart}
                    className="px-8 py-3 rounded font-['Outfit'] text-sm tracking-widest transition-all hover:brightness-110"
                    style={{ background: selectedChar.color, color: '#050505' }}
                    data-testid="confirm-start-btn"
                  >
                    BEGIN THIS PATH
                  </button>
                  <button
                    onClick={() => setSelectedPath(null)}
                    className="px-6 py-3 bg-[#050505] border border-[#1A2535] rounded font-['Outfit'] text-[#E8E0D0]/40 text-sm tracking-widest hover:text-[#E8E0D0]/70 hover:border-[#E8E0D0]/20 transition-colors"
                    data-testid="cancel-selection-btn"
                  >
                    BACK
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
          </>
        )}
      </motion.div>
    </div>
  );
};

// ─── Stat Bar ───
// ─── Send Cash Panel ───
const SendCashPanel = ({ gameId, onTransfer }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleSend = async () => {
    if (!email.trim() || !amount || parseInt(amount) <= 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/game/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, recipient_email: email.trim(), amount: parseInt(amount) }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', msg: data.message });
        setEmail('');
        setAmount('');
        if (onTransfer) onTransfer();
      } else {
        setResult({ type: 'error', msg: data.detail || 'Transfer failed' });
      }
    } catch (e) {
      setResult({ type: 'error', msg: 'Network error' });
    }
    setSending(false);
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/game/transfers/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.transfers || []);
      }
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg overflow-hidden">
      <button onClick={() => { setOpen(!open); if (!open) loadHistory(); }}
        className="w-full flex items-center justify-between p-3 hover:bg-[#C9A84C]/5 transition-colors"
        data-testid="send-cash-toggle">
        <div className="flex items-center gap-2">
          <Send size={14} className="text-[#4ADE80]" />
          <span className="font-['Outfit'] text-[#E8E0D0]/60 text-xs tracking-widest">SEND CASH</span>
        </div>
        {open ? <ChevronUp size={14} className="text-[#E8E0D0]/30" /> : <ChevronDown size={14} className="text-[#E8E0D0]/30" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <input
            type="email"
            placeholder="Player's email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full bg-[#0a0c10] border border-[#1A2535] rounded px-3 py-2 text-[#E8E0D0] font-['Outfit'] text-xs placeholder:text-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none"
            data-testid="send-cash-email"
          />
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              min="1"
              className="flex-1 bg-[#0a0c10] border border-[#1A2535] rounded px-3 py-2 text-[#4ADE80] font-['Outfit'] text-xs placeholder:text-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none"
              data-testid="send-cash-amount"
            />
            <button
              onClick={handleSend}
              disabled={sending || !email.trim() || !amount || parseInt(amount) <= 0}
              className="px-4 py-2 bg-[#4ADE80]/15 border border-[#4ADE80]/30 rounded text-[#4ADE80] font-['Outfit'] text-xs tracking-widest hover:bg-[#4ADE80]/25 disabled:opacity-30 transition-all"
              data-testid="send-cash-btn"
            >
              {sending ? '...' : 'SEND'}
            </button>
          </div>

          {result && (
            <p className={`font-['Outfit'] text-[10px] ${result.type === 'success' ? 'text-[#4ADE80]' : 'text-[#FF4444]'}`} data-testid="send-cash-result">
              {result.msg}
            </p>
          )}

          {/* Recent transfers */}
          {history.length > 0 && (
            <div className="mt-2">
              <button onClick={() => setShowHistory(!showHistory)}
                className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest hover:text-[#E8E0D0]/40 transition-colors"
                data-testid="transfer-history-toggle">
                RECENT ({history.length})
              </button>
              {showHistory && (
                <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                  {history.slice(0, 10).map(t => (
                    <div key={t.id} className="flex items-center justify-between py-1 border-b border-[#1A2535]/50">
                      <div>
                        <span className={`font-['Outfit'] text-[10px] ${t.direction === 'sent' ? 'text-[#FF4444]' : 'text-[#4ADE80]'}`}>
                          {t.direction === 'sent' ? '- ' : '+ '}${t.amount?.toLocaleString()}
                        </span>
                        <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] ml-2">
                          {t.direction === 'sent' ? `to ${t.recipient_name}` : `from ${t.sender_name}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatBar = ({ label, value, max = 100, color = "#C9A84C", icon }) => (
  <div className="flex items-center gap-2">
    {icon}
    <span className="font-['Outfit'] text-[#E8E0D0]/50 text-[10px] tracking-wider w-16 shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-[#1A2535] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (value / max) * 100)}%`, backgroundColor: color }} />
    </div>
    <span className="font-['Outfit'] text-[#C9A84C] text-[10px] w-8 text-right">{value}</span>
  </div>
);

// ─── Energy Bar ───
const EnergyBar = ({ energy, maxEnergy, onRefill, onDailyRefill, cash, refillCost, loading, innerCircle, dailyRefillAvailable }) => {
  const pct = (energy / maxEnergy) * 100;
  const barColor = pct > 60 ? '#4ADE80' : pct > 30 ? '#C9A84C' : '#8B1A1A';
  return (
    <div className="flex items-center gap-2">
      <Battery size={14} className="text-[#4ADE80]/70" />
      <div className="w-20 h-2 bg-[#1A2535] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: barColor }} />
      </div>
      <span className="font-['Outfit'] text-[#E8E0D0]/70 text-[11px]" data-testid="energy-display">{energy}/{maxEnergy}</span>
      {energy < maxEnergy && innerCircle && dailyRefillAvailable && (
        <button onClick={onDailyRefill} disabled={loading}
          className="font-['Outfit'] text-[9px] text-[#C9A84C] tracking-widest hover:text-[#C9A84C] transition-colors px-1 border border-[#C9A84C]/30 rounded"
          data-testid="daily-refill-btn">
          FREE
        </button>
      )}
      {energy < maxEnergy && cash >= refillCost && (
        <button onClick={onRefill} disabled={loading}
          className="font-['Outfit'] text-[9px] text-[#4ADE80]/60 tracking-widest hover:text-[#4ADE80] transition-colors px-1"
          data-testid="energy-refill-btn">
          REFILL
        </button>
      )}
    </div>
  );
};

// ─── Jobs Tab ───
const TIER_COLORS = {
  common: { bg: 'bg-[#E8E0D0]/5', border: 'border-[#E8E0D0]/15', text: 'text-[#E8E0D0]/40', label: 'COMMON' },
  uncommon: { bg: 'bg-[#4A7AB5]/5', border: 'border-[#4A7AB5]/20', text: 'text-[#4A7AB5]', label: 'UNCOMMON' },
  rare: { bg: 'bg-[#9F7AEA]/5', border: 'border-[#9F7AEA]/20', text: 'text-[#9F7AEA]', label: 'RARE' },
  epic: { bg: 'bg-[#C9A84C]/5', border: 'border-[#C9A84C]/20', text: 'text-[#C9A84C]', label: 'EPIC' },
};

// JobsTab and TerritoryTab replaced by StoryModeMap (Leaflet)
// Old SVG-based components removed

// ─── Crew Tab ───
const CrewTab = ({ crew, crewAssignments, territories, onRecruit, onAssign, onUnassign, loading, equippedWeapons, equippedVehicles, currentEra }) => {
  const [assignModal, setAssignModal] = useState(null);
  const ownedTerritories = territories?.filter(t => t.owned) || [];
  const eraWeapons = equippedWeapons?.[String(currentEra)] || {};
  const eraVehicles = equippedVehicles?.[String(currentEra)] || {};

  return (
    <div className="space-y-2">
      {/* Player's equipped gear */}
      {(eraWeapons['_self'] || eraVehicles['_self']) ? (
        <div className="bg-[#0a0e12] border border-[#C9A84C]/15 rounded-lg p-3 mb-3">
          <p className="font-['Outfit'] text-[9px] tracking-widest text-[#C9A84C]/40 mb-2">YOUR LOADOUT</p>
          <div className="flex gap-2 flex-wrap">
            {eraWeapons['_self'] && (
              <span className="font-['Outfit'] text-[10px] px-3 py-1 rounded bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 text-[#8B1A1A]">
                {eraWeapons['_self'].name}
              </span>
            )}
            {eraVehicles['_self'] && (
              <span className="font-['Outfit'] text-[10px] px-3 py-1 rounded bg-[#4A7AB5]/10 border border-[#4A7AB5]/20 text-[#4A7AB5]">
                {eraVehicles['_self'].name}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-[#0a0e12] border border-[#1A2535] rounded-lg p-3 mb-3" data-testid="equip-hint">
          <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[10px]">
            <span className="text-[#C9A84C]/60">TIP:</span> Buy weapons &amp; vehicles from the <span className="text-[#C9A84C]/60">STORE</span>, then go to <span className="text-[#C9A84C]/60">INVENTORY</span> and tap <span className="text-[#8B1A1A]">EQUIP</span> to assign them to yourself or a crew member.
          </p>
        </div>
      )}
      {assignModal && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center px-4" onClick={() => setAssignModal(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-[#111820] border border-[#C9A84C]/30 rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h4 className="font-['Outfit'] text-[#C9A84C] text-sm tracking-widest mb-4">ASSIGN {assignModal.name?.toUpperCase()}</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {ownedTerritories.map(t => (
                <div key={t.id} className="bg-[#0a0e12] border border-[#1A2535] rounded p-3">
                  <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold mb-2">{t.name} <span className="text-[#E8E0D0]/30">({t.tier_label})</span></p>
                  <div className="flex gap-1">
                    {['enforcer', 'earner', 'connector'].map(role => {
                      const maxSlots = t.slots?.[role] || 0;
                      const filled = t.assigned_crew?.[role]?.length || 0;
                      const available = maxSlots - filled;
                      return (
                        <button key={role} disabled={loading || available <= 0}
                          onClick={() => { onAssign(assignModal.id, t.id, role); setAssignModal(null); }}
                          className={`flex-1 px-2 py-1.5 rounded font-['Outfit'] text-[9px] tracking-widest border transition-colors ${
                            available > 0 ? 'border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10' : 'border-[#1A2535] text-[#E8E0D0]/15 cursor-not-allowed'
                          }`}>
                          {role.toUpperCase()} ({filled}/{maxSlots})
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {ownedTerritories.length === 0 && (
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-4">No territories owned. Buy territory first.</p>
              )}
            </div>
            <button onClick={() => setAssignModal(null)}
              className="mt-4 w-full py-2 border border-[#1A2535] rounded text-[#E8E0D0]/40 font-['Outfit'] text-xs hover:text-[#E8E0D0]/60 transition-colors">
              CANCEL
            </button>
          </motion.div>
        </div>
      )}

      {crew?.map(c => {
        const isAssigned = crewAssignments?.[c.id];
        return (
          <div key={c.id} className={`bg-[#111820] border rounded-lg overflow-hidden ${c.recruited ? 'border-[#C9A84C]/20' : 'border-[#1A2535]'}`}
            data-testid={`crew-${c.id}`}>
            <div className="flex gap-3 p-4">
              {/* Crew portrait */}
              {CREW_PORTRAITS[c.id] && (
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 border border-[#1A2535]">
                  <img src={CREW_PORTRAITS[c.id]} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h5 className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{c.name}</h5>
                      {c.classified && <span className="font-['Outfit'] text-[#8B1A1A] text-[8px] tracking-widest px-1 border border-[#8B1A1A]/30 rounded">CLASSIFIED</span>}
                    </div>
                <p className="font-['Outfit'] text-[#C9A84C]/50 text-[11px] mt-0.5">{c.ability}</p>
                <div className="flex gap-3 mt-1">
                  <span className="font-['Outfit'] text-[#8B1A1A]/60 text-[10px]">ATK: {c.attack}</span>
                  <span className="font-['Outfit'] text-[#4A7AB5]/60 text-[10px]">DEF: {c.defense}</span>
                </div>
                {c.recruited && isAssigned && (
                  <p className="font-['Outfit'] text-[#C9A84C]/40 text-[9px] mt-1">
                    Deployed: {isAssigned.role} @ {isAssigned.territory_id}
                  </p>
                )}
                {/* Equipped gear */}
                {c.recruited && (eraWeapons[c.id] || eraVehicles[c.id]) && (
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {eraWeapons[c.id] && (
                      <span className="font-['Outfit'] text-[8px] tracking-widest px-2 py-0.5 rounded bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 text-[#8B1A1A]">
                        ARMED: {eraWeapons[c.id].name}
                      </span>
                    )}
                    {eraVehicles[c.id] && (
                      <span className="font-['Outfit'] text-[8px] tracking-widest px-2 py-0.5 rounded bg-[#4A7AB5]/10 border border-[#4A7AB5]/20 text-[#4A7AB5]">
                        RIDES: {eraVehicles[c.id].name}
                      </span>
                    )}
                  </div>
                )}
                {c.recruited && !eraWeapons[c.id] && !eraVehicles[c.id] && (
                  <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] mt-1 italic">No gear equipped — assign from Inventory</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.recruited ? (
                  <>
                    {isAssigned ? (
                      <button onClick={() => onUnassign(c.id)} disabled={loading}
                        className="px-2 py-1 border border-[#8B1A1A]/30 rounded text-[#8B1A1A] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#8B1A1A]/10 disabled:opacity-30"
                        data-testid={`unassign-crew-${c.id}`}>
                        <UserMinus size={12} />
                      </button>
                    ) : (
                      <button onClick={() => setAssignModal(c)} disabled={loading}
                        className="px-2 py-1 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#C9A84C]/10 disabled:opacity-30"
                        data-testid={`assign-crew-${c.id}`}>
                        <UserPlus size={12} />
                      </button>
                    )}
                    <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest">IN CREW</span>
                  </>
                ) : (
                  <button onClick={() => onRecruit(c.id)} disabled={loading || !c.can_afford}
                    className="px-3 py-1.5 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[10px] tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`recruit-crew-${c.id}`}>
                    {c.cost === 0 ? 'FREE' : `$${c.cost?.toLocaleString()}`}
                  </button>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Influence Tab ───
const InfluenceTab = ({ game, pendingInfluence, onCollect, onUpgrade, loading }) => {
  const upgrades = {
    energy_regen_1: { name: "Energy Regen Boost", desc: "+1 energy/min faster regen", cost_ip: 50, tier: "street" },
    job_success_1: { name: "Job Success +5%", desc: "All jobs +5% success rate", cost_ip: 75, tier: "street" },
    territory_xp_1: { name: "Territory XP +5", desc: "+5 XP per territory cycle", cost_ip: 100, tier: "street" },
    income_10: { name: "Income +10%", desc: "All territory income +10% permanent", cost_ip: 300, tier: "borough" },
    earner_cap_up: { name: "Earner Cap +20%", desc: "Max earner bonus raised to 80%", cost_ip: 500, tier: "borough" },
    offline_double: { name: "Offline Income 2x", desc: "Offline accumulation doubles", cost_ip: 1000, tier: "empire" },
    free_crew: { name: "Free Named Crew", desc: "Choose 1 named crew member free", cost_ip: 2000, tier: "empire" },
    all_dossiers: { name: "Classified Intel", desc: "All classified dossiers unlocked", cost_ip: 5000, tier: "reaper" },
  };
  const owned = game?.influence_upgrades || [];
  const ip = game?.influence_points || 0;
  const hasPending = pendingInfluence && pendingInfluence.ip > 0;

  const tierColors = { street: '#E8E0D0', borough: '#4A7AB5', empire: '#C9A84C', reaper: '#8B1A1A' };

  return (
    <div className="space-y-4">
      <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-4 flex items-center justify-between">
        <div>
          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] tracking-widest">INFLUENCE POINTS</p>
          <p className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#C9A84C]" data-testid="influence-points">{ip}</p>
        </div>
        {hasPending && (
          <button onClick={onCollect} disabled={loading}
            className="px-4 py-2 bg-[#C9A84C]/15 border border-[#C9A84C]/40 rounded text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 transition-colors disabled:opacity-50"
            data-testid="collect-influence-btn">
            COLLECT +{pendingInfluence.ip} IP
          </button>
        )}
      </div>

      <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[10px] tracking-widest">
        Influence ticks every 2 hours from your territories. Spend IP on permanent upgrades.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.entries(upgrades).map(([id, u]) => {
          const isOwned = owned.includes(id);
          const canAfford = ip >= u.cost_ip;
          return (
            <div key={id} className={`bg-[#111820] border rounded-lg p-3 ${isOwned ? 'border-[#C9A84C]/30' : 'border-[#1A2535]'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{u.name}</h5>
                    <span className="font-['Outfit'] text-[9px] px-1 rounded" style={{ color: tierColors[u.tier], borderColor: tierColors[u.tier] + '40', borderWidth: 1 }}>
                      {u.tier.toUpperCase()}
                    </span>
                  </div>
                  <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] mt-0.5">{u.desc}</p>
                </div>
                {isOwned ? (
                  <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest shrink-0">OWNED</span>
                ) : (
                  <button onClick={() => onUpgrade(id)} disabled={loading || !canAfford}
                    className="px-3 py-1 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[10px] tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                    data-testid={`upgrade-${id}`}>
                    {u.cost_ip} IP
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Inventory Tab ───
const CATEGORY_LABELS = {
  weapon: 'WEAPONS',
  deal_of_the_day: "MATT'S DEALS",
  daily_perks: 'DAILY PERKS',
  crew_members: 'CREW',
  named_legends: 'LEGENDS',
  territory_upgrades: 'TERRITORY',
  territory_protection: 'PROTECTION',
  energy_packs: 'ENERGY',
  xp_chips: 'XP CHIPS',
  influence_packs: 'INFLUENCE',
  respect_tokens: 'RESPECT',
  property_deeds: 'PROPERTIES',
  matt_bundles: 'MATT BUNDLES',
  street_cash_packs: 'CASH PACKS',
};

const CATEGORY_COLORS = {
  weapon: '#8B1A1A',
  deal_of_the_day: '#C9A84C',
  crew_members: '#4A7AB5',
  named_legends: '#9F7AEA',
  territory_upgrades: '#4ADE80',
  property_deeds: '#C9A84C',
};

const InventoryTab = ({ gameId, crew, crewNameMap, onRefresh }) => {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selling, setSelling] = useState(null);
  const [using, setUsing] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [equipModal, setEquipModal] = useState(null); // { item, index }

  const loadInventory = async () => {
    try {
      const res = await fetch(`${API}/api/store/inventory/${gameId}`);
      if (res.ok) setInventory(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadInventory(); }, [gameId]);

  const handleSell = async (itemIndex) => {
    setSelling(itemIndex);
    try {
      const res = await fetch(`${API}/api/store/inventory/sell`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, item_index: itemIndex }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setActionResult({ type: 'sell', msg: `Sold ${data.item_name} for $${data.resale_value?.toLocaleString()}` });
          setTimeout(() => setActionResult(null), 3000);
          loadInventory();
          if (onRefresh) onRefresh();
        }
      }
    } catch (e) { console.error(e); }
    setSelling(null);
  };

  const handleUse = async (itemIndex, crewTarget) => {
    setUsing(itemIndex);
    setEquipModal(null);
    try {
      const res = await fetch(`${API}/api/store/inventory/use`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, item_index: itemIndex, crew_target: crewTarget || null }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActionResult({ type: 'use', msg: data.message });
        setTimeout(() => setActionResult(null), 4000);
        loadInventory();
        // Refresh the parent game state so energy/cash/XP updates visually
        if (onRefresh) onRefresh();
      } else {
        setActionResult({ type: 'error', msg: data.detail || data.message || 'Cannot use this item' });
        setTimeout(() => setActionResult(null), 3000);
      }
    } catch (e) {
      console.error(e);
      setActionResult({ type: 'error', msg: 'Connection error. Try again.' });
      setTimeout(() => setActionResult(null), 3000);
    }
    setUsing(null);
  };

  const isEquipType = (item) => {
    const cat = (item.category || '').toLowerCase();
    if (cat === 'vehicles' || cat === 'vehicle' || cat === 'weapon' || cat === 'weapons') return true;
    // Also detect by name keywords
    const name = (item.name || '').toLowerCase();
    const weaponWords = ['ak-47', 'glock', 'mac-10', 'uzi', 'shotgun', 'revolver', 'beretta', 'tech-9', 'pistol', 'rifle', 'desert eagle', 'mp5'];
    const vehicleWords = ['range rover', 'cadillac', 'escalade', 'bmw', 'mercedes', 'lexus', 'jeep', 'wrangler', 'impala', 'phantom', 'bentley', 'hummer'];
    return weaponWords.some(w => name.includes(w)) || vehicleWords.some(v => name.includes(v));
  };

  const getUseLabel = (item) => isEquipType(item) ? 'EQUIP' : 'USE';

  const getUseColor = (item) => {
    const cat = (item.category || '').toLowerCase();
    if (cat === 'vehicles' || cat === 'vehicle') return '#4A7AB5';
    if (cat === 'weapon' || cat === 'weapons') return '#8B1A1A';
    return '#C9A84C';
  };

  const handleEquipClick = (item, index) => {
    if (isEquipType(item)) {
      setEquipModal({ item, index });
    } else {
      handleUse(index);
    }
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-8">Loading inventory...</p>;

  const items = inventory?.inventory || [];

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <PackageOpen size={32} className="text-[#C9A84C]/20 mx-auto mb-3" />
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm">Your stash is empty.</p>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs mt-1">Items you buy from the store will show up here.</p>
      </div>
    );
  }

  const grouped = inventory?.grouped || {};
  const crewList = crew || [];

  // Use the complete crew name map from backend (all eras)
  const getCrewName = (id) => (crewNameMap && crewNameMap[id]) || id;

  return (
    <div className="space-y-4">
      {/* Action result toast */}
      <AnimatePresence>
        {actionResult && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`rounded-lg p-3 ${
              actionResult.type === 'error' ? 'bg-[#8B1A1A]/10 border border-[#8B1A1A]/30' :
              actionResult.type === 'sell' ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30' :
              'bg-[#C9A84C]/10 border border-[#C9A84C]/30'
            }`}>
            <span className={`font-['Outfit'] text-xs ${
              actionResult.type === 'error' ? 'text-[#8B1A1A]' :
              actionResult.type === 'sell' ? 'text-[#4ADE80]' : 'text-[#C9A84C]'
            }`}>{actionResult.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Equip Modal — select crew member or self */}
      <AnimatePresence>
        {equipModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setEquipModal(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0c10] border border-[#C9A84C]/20 rounded-lg max-w-sm w-full p-5"
              onClick={e => e.stopPropagation()}>
              <h4 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C] mb-1">EQUIP: {equipModal.item.name}</h4>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] mb-4">{equipModal.item.effects}</p>

              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-[10px] tracking-widest mb-3">ASSIGN TO:</p>

              {/* Self option */}
              <button onClick={() => handleUse(equipModal.index, '_self')}
                disabled={using === equipModal.index}
                className="w-full mb-2 p-3 bg-[#111820] border border-[#C9A84C]/20 rounded-lg text-left hover:border-[#C9A84C]/40 transition-colors group"
                data-testid="equip-self">
                <h5 className="font-['Outfit'] text-[#C9A84C] text-xs font-semibold group-hover:text-[#d4b55d] transition-colors">YOURSELF</h5>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Personal equipment — active on your character</p>
              </button>

              {/* Crew members */}
              {crewList.length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {crewList.map(crewId => (
                    <button key={crewId} onClick={() => handleUse(equipModal.index, crewId)}
                      disabled={using === equipModal.index}
                      className="w-full p-2.5 bg-[#111820] border border-[#1A2535] rounded-lg text-left hover:border-[#C9A84C]/30 transition-colors group"
                      data-testid={`equip-crew-${crewId}`}>
                      <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold group-hover:text-[#C9A84C] transition-colors">{getCrewName(crewId)}</h5>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] text-center py-2">No crew members to equip</p>
              )}

              <button onClick={() => setEquipModal(null)}
                className="w-full mt-3 py-2 font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#E8E0D0]/50 transition-colors"
                data-testid="equip-cancel">CANCEL</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between">
        <span className="font-['Outfit'] text-[#C9A84C]/60 text-[10px] tracking-widest">{items.length} ITEMS OWNED</span>
        <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">USE / EQUIP / SELL</span>
      </div>
      {Object.entries(grouped).map(([cat, catItems]) => {
        const color = CATEGORY_COLORS[cat] || '#C9A84C';
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
              <span className="font-['Outfit'] text-[10px] tracking-widest" style={{ color: color + 'AA' }}>
                {CATEGORY_LABELS[cat] || cat.toUpperCase().replace(/_/g, ' ')}
              </span>
              <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">({catItems.length})</span>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {catItems.map((item) => {
                const useColor = getUseColor(item);
                const useLabel = getUseLabel(item);
                return (
                <div key={`${item.item_id}-${item.index}`} className="bg-[#111820] border border-[#1A2535] rounded-lg p-3 hover:border-[#C9A84C]/20 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold truncate">
                        {item.name}
                        {item.quantity > 1 && <span className="text-[#C9A84C]/60 ml-1.5">x{item.quantity}</span>}
                      </h5>
                      <p className="font-['Outfit'] text-[#C9A84C]/50 text-[10px] mt-0.5">{item.effects}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => handleEquipClick(item, item.index)}
                        disabled={using === item.index}
                        className="px-2.5 py-1.5 rounded font-['Outfit'] text-[9px] tracking-widest transition-colors disabled:opacity-30"
                        style={{ backgroundColor: useColor + '10', color: useColor, border: `1px solid ${useColor}30` }}
                        data-testid={`use-item-${item.index}`}
                      >
                        {using === item.index ? '...' : useLabel}
                      </button>
                      <button
                        onClick={() => handleSell(item.index)}
                        disabled={selling === item.index}
                        className="px-2 py-1.5 bg-[#4ADE80]/5 border border-[#4ADE80]/15 rounded text-[#4ADE80]/50 font-['Outfit'] text-[8px] tracking-widest hover:bg-[#4ADE80]/15 hover:text-[#4ADE80] disabled:opacity-30 transition-colors"
                        data-testid={`sell-item-${item.index}`}
                      >
                        {selling === item.index ? '...' : `$${item.resale_value?.toLocaleString()}`}
                      </button>
                      {item.quantity > 1 && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Sell all ${item.quantity}x ${item.name} for ~$${(item.resale_value * item.quantity).toLocaleString()}?`)) return;
                            setSelling(item.index);
                            try {
                              const res = await fetch(`${API}/api/store/inventory/sell-all`, {
                                method: 'POST', headers: {'Content-Type': 'application/json'},
                                body: JSON.stringify({game_id: gameId, item_name: item.name, item_category: item.category})
                              });
                              const d = await res.json();
                              if (d.success) {
                                setActionResult(`Sold ${d.count_sold}x ${d.item_name} for $${d.total_value?.toLocaleString()}`);
                                loadInventory();
                                if (onRefresh) onRefresh();
                              }
                            } catch(e) { console.error('sell-all failed', e); }
                            setSelling(null);
                          }}
                          disabled={selling === item.index}
                          className="px-2 py-1.5 bg-[#E8445A]/5 border border-[#E8445A]/15 rounded text-[#E8445A]/60 font-['Outfit'] text-[7px] tracking-widest hover:bg-[#E8445A]/15 hover:text-[#E8445A] disabled:opacity-30 transition-colors"
                          data-testid={`sell-all-${item.index}`}
                        >
                          SELL ALL
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ─── Stats Tab ───
const StatsTab = ({ game, eraData, onPrestige }) => {
  const [prestigeLoading, setPrestigeLoading] = useState(false);
  const canPrestige = game?.max_era_reached >= 4 && game?.boss_defeated?.['4'];
  const cycle = game?.prestige_cycle || 0;

  const handlePrestige = async () => {
    if (!window.confirm('PRESTIGE: You will restart from Era 1 with scaled difficulty. You keep Water Crystals, Loyalty Points, and 2 crew. Cash, territories, and inventory are reset. Continue?')) return;
    setPrestigeLoading(true);
    try {
      const res = await fetch(`${API}/api/game/${game.id}/prestige`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.ok) { onPrestige(); }
    } catch (e) { console.error(e); }
    setPrestigeLoading(false);
  };

  return (
  <div className="space-y-4">
    {/* Prestige Info */}
    {cycle > 0 && (
      <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest">PRESTIGE CYCLE {cycle}</span>
            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C9A84C]">{game?.prestige_title}</h4>
          </div>
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{(1.5 ** cycle).toFixed(1)}x DIFFICULTY</span>
        </div>
      </div>
    )}

    <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-4" data-testid="stats-panel">
      <h4 className="font-['Outfit'] text-[#C9A84C] text-xs tracking-widest mb-4">PLAYER STATS</h4>
      <div className="grid grid-cols-2 gap-3 text-sm font-['Outfit']">
        <div><span className="text-[#E8E0D0]/40">Path:</span> <span className="text-[#E8E0D0]">{game?.path_name}</span></div>
        <div><span className="text-[#E8E0D0]/40">Level:</span> <span className="text-[#C9A84C]">{game?.level} — {game?.level_title}</span></div>
        <div><span className="text-[#E8E0D0]/40">Cash:</span> <span className="text-[#C9A84C]">${game?.cash?.toLocaleString()}</span></div>
        <div><span className="text-[#E8E0D0]/40">Respect Rank:</span> <span className="text-[#C9A84C]">{game?.respect_rank}</span></div>
        <div><span className="text-[#E8E0D0]/40">Territories:</span> <span className="text-[#E8E0D0]">{game?.territories_owned?.length}</span></div>
        <div><span className="text-[#E8E0D0]/40">Crew Size:</span> <span className="text-[#E8E0D0]">{game?.crew?.length}</span></div>
        <div><span className="text-[#E8E0D0]/40">Influence:</span> <span className="text-[#C9A84C]">{game?.influence_points} IP</span></div>
        <div><span className="text-[#E8E0D0]/40">Passive/3min:</span> <span className="text-[#C9A84C]">${game?.passive_per_cycle?.toLocaleString()}</span></div>
        <div><span className="text-[#E8E0D0]/40">Streak:</span> <span className="text-[#E8E0D0]">{game?.streak_count}x</span></div>
        <div><span className="text-[#E8E0D0]/40">Prestige:</span> <span className="text-[#C9A84C]">Cycle {cycle}</span></div>
        {game?.inner_circle && (
          <div className="col-span-2"><span className="font-['Outfit'] text-[9px] tracking-widest px-2 py-0.5 bg-[#C9A84C] text-[#050505] rounded font-semibold">INNER CIRCLE MEMBER</span></div>
        )}
      </div>
    </div>

    <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg p-4">
      <h4 className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest mb-3">CORE STATS</h4>
      <div className="space-y-2">
        <StatBar label="STREET" value={game?.stats?.street_smart || 0} max={50} color="#C9A84C" icon={<Zap size={10} className="text-[#C9A84C]/50" />} />
        <StatBar label="MUSCLE" value={game?.stats?.muscle || 0} max={50} color="#8B1A1A" icon={<Shield size={10} className="text-[#8B1A1A]/50" />} />
        <StatBar label="CONNECT" value={game?.stats?.connections || 0} max={50} color="#4A7AB5" icon={<Users size={10} className="text-[#4A7AB5]/50" />} />
        <StatBar label="HEAT" value={game?.stats?.heat || 0} max={100} color="#FF4444" icon={<Flame size={10} className="text-[#FF4444]/50" />} />
        <StatBar label="RESPECT" value={game?.stats?.respect || 0} max={100} color="#C9A84C" icon={<Award size={10} className="text-[#C9A84C]/50" />} />
      </div>
    </div>

    {game?.unlocked_lore?.length > 0 && (
      <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={14} className="text-[#C9A84C]/60" />
          <span className="font-['Outfit'] text-[#C9A84C]/60 text-[10px] tracking-widest">UNLOCKED LORE</span>
        </div>
        {game.unlocked_lore.map((lore, i) => (
          <p key={i} className="font-['Outfit'] text-[#E8E0D0]/40 text-xs italic mb-1">{lore}</p>
        ))}
      </div>
    )}

    {/* Prestige Button */}
    {canPrestige && (
      <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/30 rounded-lg p-4 text-center">
        <h4 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C] mb-1">THE CYCLE</h4>
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-3">
          Restart from Era 1 with {(1.5 ** (cycle + 1)).toFixed(1)}x difficulty. Keep crystals, loyalty, 2 crew.
        </p>
        <p className="font-['Outfit'] text-[#C9A84C]/60 text-[10px] mb-4">
          Next title: <span className="text-[#C9A84C] font-bold">{
            cycle === 0 ? 'Street Legend' : cycle === 1 ? 'Ghost King' : cycle === 2 ? 'The Reaper Reborn' : cycle === 3 ? 'Water Sovereign' : 'The Eternal'
          }</span>
        </p>
        <button onClick={handlePrestige} disabled={prestigeLoading}
          className="px-6 py-2 bg-[#C9A84C]/15 border border-[#C9A84C]/50 rounded text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 disabled:opacity-30 transition-colors"
          data-testid="prestige-btn">
          {prestigeLoading ? 'PRESTIGE...' : 'ENTER THE CYCLE'}
        </button>
      </div>
    )}
  </div>
  );
};

// ─── Properties Tab (part of Territory now, but keeping for legacy items) ───
const PropertiesTab = ({ properties, onBuy, onCollect, dailyIncome, loading }) => (
  <div className="space-y-2">
    {dailyIncome > 0 && (
      <button onClick={onCollect} disabled={loading}
        className="w-full py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-xs tracking-wider hover:bg-[#C9A84C]/20 transition-colors disabled:opacity-50 mb-3"
        data-testid="collect-property-income-btn">
        COLLECT ${dailyIncome?.toLocaleString()}/DAY
      </button>
    )}
    {properties?.map(p => (
      <div key={p.id} className={`bg-[#111820] border rounded-lg p-4 ${p.owned ? 'border-[#C9A84C]/20' : 'border-[#1A2535]'}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={14} className={p.owned ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/30'} />
              <h5 className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{p.name}</h5>
            </div>
            <p className="font-['Outfit'] text-[#C9A84C]/50 text-[11px] mt-1 ml-6">+${p.daily_income?.toLocaleString()}/day passive income</p>
          </div>
          {p.owned ? (
            <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest">OWNED</span>
          ) : (
            <button onClick={() => onBuy(p.id)} disabled={loading || !p.can_afford}
              className="px-3 py-1.5 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[10px] tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`buy-property-${p.id}`}>
              ${p.cost?.toLocaleString()}
            </button>
          )}
        </div>
      </div>
    ))}
  </div>
);

// ─── Store Modal ───
const StoreModal = ({ game, onClose, onPurchaseComplete }) => {
  const [purchasing, setPurchasing] = useState(null);
  const [packages, setPackages] = useState(null);
  const [innerCircleInfo, setInnerCircleInfo] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/payments/packages`).then(r => r.json()).then(d => setPackages(d.packages)).catch(() => {});
    fetch(`${API}/api/payments/inner-circle`).then(r => r.json()).then(d => setInnerCircleInfo(d)).catch(() => {});
  }, []);

  const handlePurchase = async (packageId) => {
    if (!game?.id || purchasing) return;
    setPurchasing(packageId);
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, game_id: game.id, origin_url: origin })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Purchase failed');
      }
    } catch (e) { console.error('Purchase error', e); }
    setPurchasing(null);
  };

  const handleSubscribe = async () => {
    if (!game?.id || purchasing) return;
    setPurchasing('inner_circle');
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API}/api/payments/inner-circle/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: game.id, origin_url: origin })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Subscription failed');
      }
    } catch (e) { console.error('Subscribe error', e); }
    setPurchasing(null);
  };

  const pkgIcons = {
    energy_refill: <Battery size={20} className="text-[#4ADE80]" />,
    premium_crew_1: <Users size={20} className="text-[#C9A84C]" />,
    era_skip: <ChevronRight size={20} className="text-[#8B1A1A]" />,
    respect_boost: <Award size={20} className="text-[#C9A84C]" />,
    cash_drop: <DollarSign size={20} className="text-[#C9A84C]" />,
  };

  const isInnerCircle = game?.inner_circle;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center px-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0a0e12] border border-[#C9A84C]/30 rounded-lg p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="store-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C]">THE BLACK MARKET</h3>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">PREMIUM SUPPLIES</p>
          </div>
          <button onClick={onClose} className="text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60 transition-colors" data-testid="store-close">
            <X size={20} />
          </button>
        </div>

        {/* Inner Circle Banner */}
        <div className={`border rounded-lg p-4 mb-4 ${isInnerCircle ? 'bg-[#C9A84C]/10 border-[#C9A84C]/40' : 'bg-gradient-to-r from-[#C9A84C]/5 to-[#111820] border-[#C9A84C]/20'}`}
          data-testid="inner-circle-banner">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#C9A84C]/15 rounded-full flex items-center justify-center shrink-0">
              <Crown size={20} className="text-[#C9A84C]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C9A84C]">INNER CIRCLE</h4>
                {isInnerCircle && <span className="font-['Outfit'] text-[9px] tracking-widest px-1.5 py-0.5 bg-[#C9A84C] text-[#050505] rounded font-semibold">ACTIVE</span>}
              </div>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-[11px]">
                {isInnerCircle ? 'All perks active. The water whispers your name.' : '$9.99/month — The ultimate edge.'}
              </p>
            </div>
            {!isInnerCircle && (
              <button onClick={handleSubscribe} disabled={!!purchasing}
                className={`px-4 py-2 rounded font-['Outfit'] text-xs tracking-widest border transition-colors shrink-0 bg-[#C9A84C]/15 border-[#C9A84C]/50 text-[#C9A84C] hover:bg-[#C9A84C]/25 ${purchasing === 'inner_circle' ? 'opacity-60' : ''} disabled:opacity-40`}
                data-testid="subscribe-inner-circle">
                {purchasing === 'inner_circle' ? '...' : '$9.99/MO'}
              </button>
            )}
          </div>
          {innerCircleInfo && (
            <div className="grid grid-cols-2 gap-1 mt-2">
              {innerCircleInfo.perks?.map((perk, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="text-[#C9A84C] text-[10px]">{isInnerCircle ? '\u2713' : '\u25C6'}</span>
                  <span className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px]">{perk}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* One-time packages */}
        <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest mb-2">ONE-TIME SUPPLIES</p>
        <div className="space-y-3">
          {packages ? Object.entries(packages).map(([pid, pkg]) => (
            <div key={pid} className="bg-[#111820] border border-[#1A2535] rounded-lg p-4 flex items-center gap-4 hover:border-[#C9A84C]/20 transition-colors">
              <div className="w-10 h-10 bg-[#C9A84C]/5 rounded-lg flex items-center justify-center shrink-0">
                {pkgIcons[pid] || <ShoppingBag size={20} className="text-[#C9A84C]/50" />}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{pkg.name}</h4>
                <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[11px]">{pkg.description}</p>
              </div>
              <button onClick={() => handlePurchase(pid)} disabled={!!purchasing}
                className={`px-4 py-2 rounded font-['Outfit'] text-xs tracking-widest border transition-colors shrink-0 ${
                  purchasing === pid ? 'bg-[#C9A84C]/30 border-[#C9A84C] text-[#C9A84C] opacity-60' :
                  'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/20'
                } disabled:opacity-40`}
                data-testid={`buy-${pid}`}>
                {purchasing === pid ? '...' : `$${pkg.amount?.toFixed(2)}`}
              </button>
            </div>
          )) : (
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-4">Loading...</p>
          )}
        </div>
        <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[9px] text-center mt-4">Secured by Stripe. Payments are final.</p>
      </motion.div>
    </div>
  );
};

// ─── Main Game Dashboard ───
const GameDashboard = ({ game, eraData, onAction, loading, onNewGame, onRefreshGame }) => {
  const [tab, setTab] = useState('jobs');
  const [showStore, setShowStore] = useState(false);
  const [showLoadout, setShowLoadout] = useState(false);
  const [showMatt, setShowMatt] = useState(false);
  const [showFullStore, setShowFullStore] = useState(false);
  const [jobAnim, setJobAnim] = useState(null);
  const [interactiveMission, setInteractiveMission] = useState(null); // { jobId }
  const [statFlash, setStatFlash] = useState(null); // { cash: +150, xp: +5, etc }
  const prevCashRef = React.useRef(game?.cash);

  // Detect stat changes and trigger flash
  React.useEffect(() => {
    if (game?.cash !== undefined && prevCashRef.current !== undefined && game.cash !== prevCashRef.current) {
      const delta = game.cash - prevCashRef.current;
      setStatFlash({ cash: delta });
      const timer = setTimeout(() => setStatFlash(null), 3000);
      return () => clearTimeout(timer);
    }
    prevCashRef.current = game?.cash;
  }, [game?.cash]);

  if (!game || !eraData) return null;

  const isFinalEra = game.current_era === 4;

  const tabs = [
    { id: 'jobs', label: game?.faction === 'badge' ? 'OPS' : 'JOBS', icon: <Crosshair size={14} />, count: game?.faction === 'badge' ? null : eraData.jobs?.filter(j => !j.completed).length },
    { id: 'modes', label: 'MODES', icon: <Target size={14} /> },
    { id: 'flights', label: game.has_active_flight ? 'FLIGHTS (IN COUNTRY)' : 'FLIGHTS', icon: <Globe size={14} />, highlight: game.has_active_flight },
    { id: 'gambling', label: 'GAMBLING', icon: <Dice5 size={14} /> },
    { id: 'block', label: 'RUN THE BLOCK', icon: <Gamepad2 size={14} /> },
    { id: 'territory', label: 'TERRITORY', icon: <MapPin size={14} />, count: `${eraData.territory_owned}/${eraData.territories?.length || 0}` },
    { id: 'crew', label: 'CREW', icon: <Users size={14} />, count: game.crew?.length },
    { id: 'inventory', label: 'INVENTORY', icon: <PackageOpen size={14} /> },
    { id: 'influence', label: 'INFLUENCE', icon: <Crown size={14} />, count: game.influence_points || 0 },
    { id: 'stats', label: 'STATS', icon: <Gauge size={14} /> },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-[#E8E0D0]">
      <div className="film-grain" />
      {showStore && <StoreModal game={game} onClose={() => setShowStore(false)} onPurchaseComplete={onRefreshGame} />}
      {showLoadout && (
        <CrewLoadout
          gameId={game.id}
          game={game}
          crew={eraData?.crew || []}
          onClose={() => setShowLoadout(false)}
          onRefresh={() => { setShowLoadout(false); onRefreshGame(); }}
        />
      )}
      {/* Job Action Animation */}
      <AnimatePresence>
        {jobAnim && (
          <JobActionAnimation
            jobName={jobAnim.jobName}
            jobId={jobAnim.jobId}
            success={game.job_result?.success ?? true}
            reward={game.job_result?.reward || 0}
            xp={game.job_result?.xp || 0}
            onComplete={() => setJobAnim(null)}
            faction={game.faction || 'cmd'}
            characterPath={game.path || 'anton'}
          />
        )}
      </AnimatePresence>
      {/* Interactive Mission Overlay */}
      <AnimatePresence>
        {interactiveMission && (
          <InteractiveMission
            gameId={game.id}
            jobId={interactiveMission.jobId}
            onComplete={(result) => {
              setInteractiveMission(null);
              if (result) onRefreshGame();
            }}
            onCancel={() => setInteractiveMission(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showMatt && (
          <BackpackMattStore
            gameId={game.id}
            onClose={() => setShowMatt(false)}
            onPurchase={() => onRefreshGame()}
            playerCash={game.cash}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showFullStore && (
          <MattStoreFull
            gameId={game.id}
            onClose={() => setShowFullStore(false)}
            onRefresh={() => onRefreshGame()}
          />
        )}
      </AnimatePresence>

      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-md border-b border-[#C9A84C]/10">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <a href="/" className="hover:opacity-80 transition-opacity shrink-0" data-testid="game-home-link">
              <img src="/kow-game-logo.png" alt="KOW" className="h-10" />
            </a>
            {game.inner_circle && (
              <span className="font-['Outfit'] text-[9px] tracking-widest px-2 py-0.5 bg-[#C9A84C] text-[#050505] rounded font-semibold" data-testid="inner-circle-badge">
                INNER CIRCLE
              </span>
            )}
            <div className="flex items-center gap-2">
              {/* Era Switcher — always visible */}
              {game.unlocked_eras && game.unlocked_eras.length > 1 ? (
                <select
                  value={game.current_era}
                  onChange={async (e) => {
                    const targetEra = parseInt(e.target.value);
                    if (targetEra === game.current_era) return;
                    try {
                      const res = await fetch(`${API}/api/game/${game.id}/switch-era`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ era: targetEra }),
                      });
                      if (res.ok) onRefreshGame();
                    } catch (err) { console.error(err); }
                  }}
                  className="font-['Outfit'] text-[10px] tracking-widest px-2 py-0.5 border rounded bg-[#050505] cursor-pointer border-[#C9A84C]/30 text-[#C9A84C] hover:border-[#C9A84C]/60 transition-all appearance-none"
                  data-testid="era-switcher"
                >
                  {game.unlocked_eras.map(era => (
                    <option key={era} value={era}>ERA {era}</option>
                  ))}
                </select>
              ) : (
                <span className={`font-['Outfit'] text-[10px] tracking-widest px-2 py-0.5 border rounded ${
                  isFinalEra ? 'text-[#C9A84C] border-[#C9A84C]/30' : 'text-[#8B1A1A] border-[#8B1A1A]/30'
                }`}>
                  ERA {game.current_era}
                </span>
              )}
              <span className="font-['Outfit'] text-[#E8E0D0]/40 text-xs hidden sm:inline">{game.era_title}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap justify-end">
            <GameMusic />
            {/* Wanted Level — Commissioner Rodriguez heat indicator */}
            {(game.stats?.heat > 0 || game.trap_refusals > 0) && (() => {
              const heat = game.stats?.heat || 0;
              const refusals = game.trap_refusals || 0;
              const stars = heat <= 20 ? 1 : heat <= 40 ? 2 : heat <= 60 ? 3 : heat <= 80 ? 4 : 5;
              const label = stars === 1 ? 'LOW' : stars === 2 ? 'MODERATE' : stars === 3 ? 'HIGH' : stars === 4 ? 'CRITICAL' : 'MOST WANTED';
              const starColor = stars <= 2 ? '#C9A84C' : stars <= 3 ? '#FF6B35' : stars <= 4 ? '#EF4444' : '#DC2626';
              return (
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded border transition-all ${
                  stars >= 4 ? 'animate-pulse' : ''
                }`}
                style={{ borderColor: `${starColor}30`, background: `${starColor}08` }}
                title={`Heat: ${heat}/100 | Refusals: ${refusals} | Rodriguez threat: ${label}`}
                data-testid="wanted-level-indicator">
                  <div className="flex gap-px">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} width="10" height="10" viewBox="0 0 24 24" fill={s <= stars ? starColor : 'none'}
                        stroke={s <= stars ? starColor : '#E8E0D020'} strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    ))}
                  </div>
                  <span className="font-['Outfit'] text-[8px] tracking-widest hidden sm:inline" style={{ color: starColor }}>
                    {label}
                  </span>
                </div>
              );
            })()}
            <div className="flex items-center gap-1.5">
              <DollarSign size={14} className="text-[#C9A84C]" />
              <span className="font-['Outfit'] text-[#C9A84C] text-sm font-semibold" data-testid="player-cash">${game.cash?.toLocaleString()}</span>
              {statFlash?.cash && (
                <motion.span
                  initial={{ opacity: 1, y: 0 }}
                  animate={{ opacity: 0, y: -15 }}
                  transition={{ duration: 2.5 }}
                  className={`font-['Outfit'] text-xs font-bold ${statFlash.cash > 0 ? 'text-[#10B981]' : 'text-[#DC2626]'}`}
                  data-testid="cash-flash"
                >
                  {statFlash.cash > 0 ? '+' : ''}${statFlash.cash.toLocaleString()}
                </motion.span>
              )}
            </div>
            {/* Active boosts */}
            {game.territory_income_multiplier > 1 && (
              <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded bg-[#4ADE80]/10 border border-[#4ADE80]/20 text-[#4ADE80] animate-pulse" data-testid="boost-income" title="Territory income boosted">
                {game.territory_income_multiplier}x INCOME
              </span>
            )}
            {game.territory_shield_until && new Date(game.territory_shield_until) > new Date() && (
              <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded bg-[#4A7AB5]/10 border border-[#4A7AB5]/20 text-[#4A7AB5]" data-testid="boost-shield" title="Territory shield active">
                SHIELD
              </span>
            )}
            {(() => {
              const era = String(game.current_era);
              const wepCount = Object.keys(game.equipped_weapons?.[era] || {}).length;
              const vehCount = Object.keys(game.equipped_vehicles?.[era] || {}).length;
              if (wepCount + vehCount === 0) return null;
              return (
                <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 text-[#8B1A1A]" data-testid="boost-equip" title={`${wepCount} weapons (+${wepCount*3}% jobs), ${vehCount} vehicles (+${vehCount*2}% jobs)`}>
                  {wepCount > 0 && `${wepCount} WPN`}{wepCount > 0 && vehCount > 0 && ' '}{vehCount > 0 && `${vehCount} VEH`}
                </span>
              );
            })()}
            <div className="hidden sm:flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9F7AEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12l4 6-10 13L2 9Z"/><path d="M11 3 8 9l4 13 4-13-3-6"/><path d="M2 9h20"/></svg>
              <span className="font-['Outfit'] text-[#9F7AEA] text-xs font-semibold" data-testid="player-crystals">{(game.water_crystals || 0).toLocaleString()}</span>
            </div>
            <EnergyBar energy={game.energy} maxEnergy={game.max_energy} cash={game.cash} refillCost={5000}
              onRefill={() => onAction('energy/refill')}
              onDailyRefill={() => onAction('daily-refill')}
              innerCircle={game.inner_circle}
              dailyRefillAvailable={game.daily_refill_available}
              loading={loading} />
            <div className="hidden sm:flex items-center gap-1">
              <Award size={12} className="text-[#C9A84C]/50" />
              <span className="font-['Outfit'] text-[#E8E0D0]/50 text-[10px]">{game.respect_rank}</span>
            </div>
            <button onClick={onNewGame}
              className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest hover:text-[#E8E0D0]/60 transition-colors"
              data-testid="new-game-btn">NEW</button>
            <button onClick={() => setShowFullStore(true)}
              className="font-['Outfit'] text-[10px] tracking-widest transition-all flex items-center gap-1 px-2 py-1 rounded border border-[#9F7AEA]/25 text-[#9F7AEA]/70 hover:text-[#9F7AEA] hover:border-[#9F7AEA]/40 hover:bg-[#9F7AEA]/5"
              data-testid="store-btn">
              <ShoppingBag size={12} /> STORE
            </button>
            <button onClick={() => setShowStore(true)}
              className="font-['Outfit'] text-[#C9A84C]/50 text-[10px] tracking-widest hover:text-[#C9A84C] transition-colors flex items-center gap-1"
              data-testid="black-market-btn">
              <DollarSign size={12} /> MARKET
            </button>
            <button onClick={() => setShowMatt(true)}
              className="font-['Outfit'] text-[10px] tracking-widest transition-all flex items-center gap-1 px-2 py-1 rounded border border-[#C9A84C]/20 text-[#C9A84C]/60 hover:text-[#C9A84C] hover:border-[#C9A84C]/40 hover:bg-[#C9A84C]/5"
              data-testid="call-matt-btn">
              <PackageOpen size={12} /> MATT
            </button>
            <a href="/leaderboard"
              className="font-['Outfit'] text-[#C9A84C]/30 text-[10px] tracking-widest hover:text-[#C9A84C]/60 transition-colors hidden sm:inline"
              data-testid="leaderboard-link">LEDGER</a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Narrator */}
        <AnimatePresence mode="wait">
          {game.narrator_message && (
            <motion.div key={game.narrator_message} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-[#0a0e12] border border-[#C9A84C]/20 rounded-lg p-4 mb-4" data-testid="narrator-message">
              <p className="font-['Cormorant_Garamond'] text-[#C9A84C]/80 italic text-sm leading-relaxed">
                "{game.narrator_message}"
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Player Card (compact) */}
            <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg overflow-hidden" data-testid="player-stats">
              {/* Character portrait — era-specific */}
              {(() => {
                const portraits = {
                  anton: { 1: CHAR_PHOTOS.anton, 2: CHAR_PHOTOS.anton },
                  vincent: { 1: CHAR_PHOTOS.vincent },
                  diego: { 1: CHAR_PHOTOS.diego, 2: CHAR_PHOTOS.diego, 3: CHAR_PHOTOS.diego, 4: CHAR_PHOTOS.diego },
                  francisco: { 1: CHAR_PHOTOS.francisco },
                  antwon: { 1: CHAR_PHOTOS.antwon },
                  filomena: { 1: CHAR_PHOTOS.filomena },
                  santo: { 1: CHAR_PHOTOS.santo },
                  fuego: { 1: CHAR_PHOTOS.fuego },
                  la_sombra: { 1: CHAR_PHOTOS.la_sombra },
                };
                const pathPortraits = portraits[game.path] || {};
                const era = game.current_era || 1;
                const src = pathPortraits[era] || pathPortraits[Math.max(...Object.keys(pathPortraits).map(Number).filter(e => e <= era))] || Object.values(pathPortraits)[0];
                const objPos = 'center 15%';
                return src ? (
                  <div className="relative h-44 overflow-hidden">
                    <img src={src} alt={game.path_name} className="w-full h-full object-cover" style={{ objectPosition: objPos }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111820] via-[#111820]/30 to-transparent" />
                  </div>
                ) : null;
              })()}
              <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-['Cormorant_Garamond'] text-base font-bold text-[#C9A84C] truncate">
                  {game.diego_evolved ? (game.evolved_name || "DARIUS 'STACKS' HENDERSON") : game.path_name}
                </h3>
                {game.faction && (
                  <span className={`font-['Outfit'] text-[9px] tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${
                    game.faction === 'cmd' ? 'text-[#C9A84C] border-[#C9A84C]/30 bg-[#C9A84C]/10' :
                    game.faction === 'morelli' ? 'text-[#8B1A1A] border-[#8B1A1A]/30 bg-[#8B1A1A]/10' :
                    game.faction === 'badge' ? 'text-[#1E90FF] border-[#1E90FF]/30 bg-[#1E90FF]/10' :
                    'text-[#4A7AB5] border-[#4A7AB5]/30 bg-[#4A7AB5]/10'
                  }`} data-testid="player-faction-badge">
                    {game.faction === 'cmd' ? 'CMD' : game.faction === 'morelli' ? 'MORELLI' : game.faction === 'badge' ? 'THE BADGE' : 'DDN'}
                  </span>
                )}
              </div>
              {game.username && game.username !== 'Anonymous' && (
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mb-0.5" data-testid="player-username">{game.username}</p>
              )}
              {game.diego_evolved && (
                <p className="font-['Outfit'] text-[#C9A84C]/40 text-[9px] tracking-widest mb-0.5" data-testid="player-location">
                  {game.evolved_alias || 'THE GHOST'} — {game.evolved_location || 'LOS ANGELES'}
                </p>
              )}
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] tracking-widest mb-2">
                {game.level_title} — LEVEL {game.level}
              </p>
              <div className="mb-2">
                <div className="flex justify-between mb-0.5">
                  <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">XP</span>
                  <span className="font-['Outfit'] text-[#C9A84C]/50 text-[9px]">{game.xp}/{game.xp_next}</span>
                </div>
                <div className="h-1 bg-[#1A2535] rounded-full overflow-hidden">
                  <div className="h-full bg-[#C9A84C] rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (game.xp / game.xp_next) * 100)}%` }} />
                </div>
              </div>
              <div className="space-y-1.5">
                <StatBar label="STREET" value={game.stats?.street_smart || 0} max={50} color="#C9A84C" icon={<Zap size={10} className="text-[#C9A84C]/50" />} />
                <StatBar label="MUSCLE" value={game.stats?.muscle || 0} max={50} color="#8B1A1A" icon={<Shield size={10} className="text-[#8B1A1A]/50" />} />
                <StatBar label="CONNECT" value={game.stats?.connections || 0} max={50} color="#4A7AB5" icon={<Users size={10} className="text-[#4A7AB5]/50" />} />
                <StatBar label="HEAT" value={game.stats?.heat || 0} max={100} color="#FF4444" icon={<Flame size={10} className="text-[#FF4444]/50" />} />
                <StatBar label="RESPECT" value={game.stats?.respect || 0} max={100} color="#C9A84C" icon={<Award size={10} className="text-[#C9A84C]/50" />} />
              </div>
              </div>
            </div>

            {/* Era Info */}
            <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg p-4">
              <p className="font-['Outfit'] text-[#8B1A1A] text-[10px] tracking-widest mb-1">ERA {game.current_era}</p>
              <h4 className="font-['Cormorant_Garamond'] text-base font-bold text-[#E8E0D0] mb-1">{eraData.era?.title}</h4>
              <p className="font-['Outfit'] text-[#C9A84C]/60 text-[10px]">{eraData.era?.years}</p>
              {game.faction === 'badge' ? (
                <>
                  <p className="font-['Outfit'] text-[#1E90FF]/60 text-xs mt-2">Operation Low Tide</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Shield size={12} className="text-[#1E90FF]/60" />
                    <span className="font-['Outfit'] text-[#E8E0D0]/50 text-xs">
                      Commissioner — Interstate Task Force
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mt-2">Rival: {eraData.era?.rival}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <MapPin size={12} className="text-[#C9A84C]/60" />
                    <span className="font-['Outfit'] text-[#E8E0D0]/50 text-xs">
                      {eraData.territory_owned}/{eraData.territory_needed} blocks to boss
                    </span>
                  </div>
                </>
              )}
              {game.passive_per_cycle > 0 && (
                <p className="font-['Outfit'] text-[#C9A84C]/40 text-[10px] mt-2">
                  Passive: ${game.passive_per_cycle?.toLocaleString()}/3min
                </p>
              )}
            </div>

            {/* Send Cash */}
            <SendCashPanel gameId={game.id} onTransfer={() => onRefreshGame()} />

            {/* Boss Fight */}
            {eraData.can_fight_boss && !eraData.boss_defeated && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-b from-[#1A1015] to-[#111820] border border-[#8B1A1A]/40 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Swords size={16} className="text-[#8B1A1A]" />
                  <span className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-widest">BOSS FIGHT</span>
                </div>
                <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{eraData.era?.boss?.name}</h4>
                <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-3">{eraData.era?.boss?.faction}</p>
                <button onClick={() => onAction('boss')} disabled={loading}
                  className="w-full py-2 bg-[#8B1A1A] text-[#E8E0D0] font-['Outfit'] text-xs tracking-widest rounded hover:bg-[#A52020] transition-colors disabled:opacity-50"
                  data-testid="boss-fight-btn">
                  ATTACK {eraData.era?.boss?.name?.toUpperCase()}
                </button>
              </motion.div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* 5 Tabs */}
            <div className="flex gap-1 mb-4 bg-[#111820] rounded-lg p-1 overflow-x-auto" data-testid="game-tabs">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded font-['Outfit'] text-xs tracking-wider transition-all shrink-0 ${
                    tab === t.id ? 'bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30' :
                    t.highlight ? 'text-[#4ADE80] animate-pulse' :
                    'text-[#E8E0D0]/40 hover:text-[#E8E0D0]/70'
                  }`}
                  data-testid={`tab-${t.id}`}>
                  {t.icon}
                  {t.label}
                  {t.count !== undefined && <span className="text-[10px] opacity-60">({t.count})</span>}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {tab === 'jobs' && (
                <motion.div key="jobs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {game.faction === 'badge' ? (
                    <PoliceJobsTab gameId={game.id} onRefresh={onRefreshGame} />
                  ) : (
                  <StoryModeMap
                    mode="jobs"
                    era={game.current_era}
                    jobs={eraData.jobs}
                    rotatingJobs={eraData.rotating_jobs}
                    rotationRefresh={eraData.rotation_refresh_in}
                    onDoJob={(id) => {
                      // Check if this is an interactive mission (e1j1, etc.)
                      const INTERACTIVE_JOBS = ['e1j1'];
                      if (INTERACTIVE_JOBS.includes(id) && !game.completed_jobs?.includes(id)) {
                        setInteractiveMission({ jobId: id });
                        return;
                      }
                      const allJobs = [...(eraData.jobs || []), ...(eraData.rotating_jobs || [])];
                      const job = allJobs.find(j => j.id === id);
                      const jobName = job?.name || 'Street Job';
                      setJobAnim({ jobName, jobId: id, pending: true });
                      onAction('job', { job_id: id });
                    }}
                    loading={loading}
                    streakCount={game.streak_count}
                  />
                  )}
                </motion.div>
              )}
              {tab === 'modes' && (
                <motion.div key="modes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <GameModesTab gameId={game.id} onRefresh={onRefreshGame} username={game.username || game.email || 'Unknown'} faction={game.faction} />
                </motion.div>
              )}
              {tab === 'flights' && (
                <motion.div key="flights" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <FlightsTab gameId={game.id} onRefresh={onRefreshGame} />
                </motion.div>
              )}
              {tab === 'gambling' && (
                <motion.div key="gambling" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <GamblingTab gameId={game.id} onRefresh={onRefreshGame} />
                </motion.div>
              )}
              {tab === 'block' && (
                <motion.div key="block" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <RunTheBlockPremium gameId={game.id} era={game.current_era} onRefresh={onRefreshGame} />
                </motion.div>
              )}
              {tab === 'territory' && (
                <motion.div key="territory" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <StoryModeMap
                    mode="territory"
                    era={game.current_era}
                    territories={eraData.territories}
                    onClaimTerritory={(id) => onAction('territory', { territory_id: id })}
                    onCollectTerritory={() => onAction('territory/collect')}
                    loading={loading}
                    territoryOwned={eraData.territory_owned}
                    territoryNeeded={eraData.territory_needed}
                    pendingTerritory={eraData.pending_territory}
                  />
                </motion.div>
              )}
              {tab === 'crew' && (
                <motion.div key="crew" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <CrewTab crew={eraData.crew} crewAssignments={game.crew_assignments}
                    territories={eraData.territories}
                    onRecruit={(id) => onAction('recruit', { crew_id: id })}
                    onAssign={(crewId, terId, role) => onAction('crew/assign', { crew_id: crewId, territory_id: terId, role })}
                    onUnassign={(id) => onAction('crew/unassign', { crew_id: id })}
                    equippedWeapons={game.equipped_weapons}
                    equippedVehicles={game.equipped_vehicles}
                    currentEra={game.current_era}
                    loading={loading} />
                  <button onClick={() => setShowLoadout(true)}
                    className="w-full mt-3 py-3 bg-[#111820] border border-[#C9A84C]/20 rounded-xl font-['Outfit'] text-[10px] text-[#C9A84C]/60 hover:text-[#C9A84C] hover:border-[#C9A84C]/40 tracking-widest transition-all"
                    data-testid="open-loadout-btn">
                    CREW LOADOUT — EQUIP WEAPONS & VEHICLES
                  </button>
                </motion.div>
              )}
              {tab === 'influence' && (
                <motion.div key="influence" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <InfluenceTab game={game} pendingInfluence={eraData.pending_influence}
                    onCollect={() => onAction('influence/collect')}
                    onUpgrade={(id) => onAction('influence/upgrade', { upgrade_id: id })}
                    loading={loading} />
                </motion.div>
              )}
              {tab === 'inventory' && (
                <motion.div key="inventory" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <InventoryTab gameId={game.id} crew={game.crew} crewNameMap={game.crew_name_map} onRefresh={onRefreshGame} />
                </motion.div>
              )}
              {tab === 'stats' && (
                <motion.div key="stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <StatsTab game={game} eraData={eraData} onPrestige={onRefreshGame} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Era Transition ───
const EraTransition = ({ eraNum, title, subtitle, onContinue }) => {
  const [phase, setPhase] = useState(0);
  const img = ERA_TRANSITION_IMAGES[eraNum] || ERA_TRANSITION_IMAGES[1];

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 200),
      setTimeout(() => setPhase(2), 1800),
      setTimeout(() => setPhase(3), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] bg-[#020304] flex items-center justify-center overflow-hidden" data-testid="era-transition">
      {/* Background image with parallax zoom */}
      <motion.div className="absolute inset-0"
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: phase >= 1 ? 1 : 1.1, opacity: phase >= 1 ? 1 : 0 }}
        transition={{ duration: 2.5, ease: 'easeOut' }}>
        <img src={img} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(2,3,4,0.3) 0%, rgba(2,3,4,0.6) 40%, rgba(2,3,4,0.95) 80%, rgba(2,3,4,1) 100%)'
        }} />
      </motion.div>

      {/* Film grain overlay */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
      }} />

      {/* Cinematic letterbox bars */}
      <motion.div className="absolute top-0 left-0 right-0 bg-black z-20"
        initial={{ height: '0%' }} animate={{ height: '12%' }}
        transition={{ duration: 1, delay: 0.3 }} />
      <motion.div className="absolute bottom-0 left-0 right-0 bg-black z-20"
        initial={{ height: '0%' }} animate={{ height: '12%' }}
        transition={{ duration: 1, delay: 0.3 }} />

      {/* Gold line sweep */}
      <motion.div className="absolute left-0 right-0 h-px z-30"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={phase >= 2 ? { scaleX: 1, opacity: [0, 1, 0.3] } : {}}
        transition={{ duration: 1.5 }}
        style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', top: '12%' }} />

      <div className="relative z-10 text-center max-w-xl px-6">
        {/* ERA COMPLETE badge */}
        <AnimatePresence>
          {phase >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20, letterSpacing: '0.1em' }}
              animate={{ opacity: 1, y: 0, letterSpacing: '0.5em' }}
              transition={{ duration: 1.2, ease: 'easeOut' }}>
              <p className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-[0.5em] mb-6">ERA {eraNum} COMPLETE</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Title with dramatic reveal */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.2, type: 'spring', stiffness: 80 }}>
              <h2 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-bold text-[#C9A84C] mb-3 leading-tight"
                style={{ textShadow: '0 0 40px rgba(201,168,76,0.3)' }}>
                {title}
              </h2>
              <motion.div className="w-24 h-px mx-auto mb-4"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{ background: 'linear-gradient(90deg, transparent, #C9A84C60, transparent)' }} />
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm italic tracking-wide">{subtitle}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Continue button */}
        <AnimatePresence>
          {phase >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}>
              <button onClick={onContinue}
                className="mt-10 px-10 py-3 rounded-lg font-['Outfit'] text-sm tracking-[0.3em] transition-all hover:brightness-125"
                style={{
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))',
                  border: '1px solid rgba(201,168,76,0.3)',
                  color: '#C9A84C',
                  boxShadow: '0 0 30px rgba(201,168,76,0.08)',
                }}
                data-testid="era-transition-continue">
                ENTER NEXT ERA <ChevronRight size={16} className="inline ml-2" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Diego → Darius Cinematic Transition ───
const DariusTransition = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 100),
      setTimeout(() => setPhase(2), 3000),
      setTimeout(() => setPhase(3), 6000),
      setTimeout(() => setPhase(4), 9000),
      setTimeout(() => setPhase(5), 12000),
      setTimeout(() => onComplete(), 15000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden" data-testid="darius-transition">
      {/* Background pulse */}
      <motion.div
        className="absolute inset-0"
        animate={{ background: phase >= 3 ? 'radial-gradient(ellipse at center, #1a0a00 0%, #000 70%)' : '#000' }}
        transition={{ duration: 2 }}
      />

      {/* Water ripple overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      <div className="relative z-10 text-center max-w-2xl px-6">
        {/* Phase 1: Diego dies */}
        <AnimatePresence mode="wait">
          {phase >= 1 && phase < 3 && (
            <motion.div
              key="death"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
            >
              <motion.p
                className="font-['Cormorant_Garamond'] text-[#8B1A1A] text-lg italic mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                "The water takes what it's owed."
              </motion.p>
              <motion.h1
                className="font-['Cormorant_Garamond'] text-5xl sm:text-7xl font-bold text-[#E8E0D0]"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.8 }}
              >
                DIEGO IS DEAD.
              </motion.h1>
              <motion.div
                className="mt-6 h-px bg-[#8B1A1A]/30 mx-auto"
                initial={{ width: 0 }}
                animate={{ width: '60%' }}
                transition={{ duration: 2, delay: 1.5 }}
              />
            </motion.div>
          )}

          {/* Phase 2: Name reveal */}
          {phase >= 3 && phase < 5 && (
            <motion.div
              key="rebirth"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
            >
              <motion.p
                className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-[0.4em] mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                NEW NAME. NEW CITY. NEW RULES.
              </motion.p>
              <motion.h1
                className="font-['Cormorant_Garamond'] text-4xl sm:text-6xl font-bold text-[#C9A84C] mb-2"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1 }}
              >
                DARIUS
              </motion.h1>
              <motion.p
                className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl text-[#C9A84C]/80 italic mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
              >
                "STACKS" HENDERSON
              </motion.p>
              <motion.p
                className="font-['Outfit'] text-[#E8E0D0]/40 text-sm tracking-widest"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.5 }}
              >
                LOS ANGELES, CALIFORNIA
              </motion.p>
            </motion.div>
          )}

          {/* Phase 3: The Ghost */}
          {phase >= 5 && (
            <motion.div
              key="ghost"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <motion.p
                className="font-['Cormorant_Garamond'] text-xl text-[#E8E0D0]/50 italic leading-relaxed mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                "But the water... the water followed you to the Pacific."
              </motion.p>
              <motion.p
                className="font-['Outfit'] text-[#C9A84C]/40 text-xs tracking-[0.3em] animate-pulse"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                THE GHOST REBORN
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Skip button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3 }}
        onClick={onComplete}
        className="absolute bottom-8 right-8 font-['Outfit'] text-[#E8E0D0]/20 text-[10px] tracking-widest hover:text-[#E8E0D0]/50 transition-colors"
        data-testid="skip-transition"
      >
        SKIP
      </motion.button>
    </div>
  );
};

// ─── Game Complete ───
const GameComplete = ({ game, onNewGame, onContinuePlaying }) => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 relative overflow-hidden">
    <div className="film-grain" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#C9A84C]/5 via-transparent to-[#050505]" />
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2 }}
      className="text-center relative z-10 max-w-2xl">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 100 }} className="mb-6">
        <Award size={48} className="text-[#C9A84C] mx-auto" />
      </motion.div>
      <p className="font-['Outfit'] text-[#C9A84C] text-xs tracking-[0.3em] mb-4" data-testid="game-complete-label">THE SAGA IS COMPLETE</p>
      <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-bold text-[#C9A84C] mb-4">KILLING OVER WATER</h1>
      <p className="font-['Cormorant_Garamond'] text-xl text-[#E8E0D0]/70 italic mb-6">
        "The water remembers everything. Every deal. Every betrayal. Every body. And now — every victory."
      </p>
      <div className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-6 mb-8 text-left">
        <h3 className="font-['Outfit'] text-[#C9A84C] text-sm tracking-widest mb-4">FINAL STATS</h3>
        <div className="grid grid-cols-2 gap-3 text-sm font-['Outfit']">
          <div><span className="text-[#E8E0D0]/40">Path:</span> <span className="text-[#E8E0D0]">{game?.path_name}</span></div>
          <div><span className="text-[#E8E0D0]/40">Level:</span> <span className="text-[#C9A84C]">{game?.level} — {game?.level_title}</span></div>
          <div><span className="text-[#E8E0D0]/40">Cash:</span> <span className="text-[#C9A84C]">${game?.cash?.toLocaleString()}</span></div>
          <div><span className="text-[#E8E0D0]/40">Territories:</span> <span className="text-[#E8E0D0]">{game?.territories_owned?.length}</span></div>
          <div><span className="text-[#E8E0D0]/40">Crew:</span> <span className="text-[#E8E0D0]">{game?.crew?.length}</span></div>
          <div><span className="text-[#E8E0D0]/40">Respect:</span> <span className="text-[#C9A84C]">{game?.stats?.respect}</span></div>
          <div><span className="text-[#E8E0D0]/40">Influence:</span> <span className="text-[#C9A84C]">{game?.influence_points} IP</span></div>
          <div><span className="text-[#E8E0D0]/40">Respect Rank:</span> <span className="text-[#C9A84C]">{game?.respect_rank}</span></div>
        </div>
        {game?.unlocked_lore?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#C9A84C]/10">
            <p className="font-['Outfit'] text-[#C9A84C]/60 text-[10px] tracking-widest mb-2">UNLOCKED LORE</p>
            {game.unlocked_lore.map((lore, i) => (
              <p key={i} className="font-['Outfit'] text-[#E8E0D0]/40 text-xs italic mb-1">{lore}</p>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        <button onClick={onContinuePlaying}
          className="px-6 py-3 bg-[#C9A84C]/20 border border-[#C9A84C]/50 rounded text-[#C9A84C] font-['Outfit'] text-sm tracking-widest hover:bg-[#C9A84C]/30 transition-colors"
          data-testid="game-complete-continue">
          {game?.path === 'diego' ? 'BECOME DARIUS' : 'CONTINUE PLAYING'}
        </button>
        <a href="/"
          className="px-6 py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded text-[#C9A84C] font-['Outfit'] text-sm tracking-widest hover:bg-[#C9A84C]/20 transition-colors"
          data-testid="game-complete-home">RETURN TO THE WATER</a>
        <button onClick={onNewGame}
          className="px-6 py-3 bg-[#8B1A1A]/20 border border-[#8B1A1A]/40 rounded text-[#8B1A1A] font-['Outfit'] text-sm tracking-widest hover:bg-[#8B1A1A]/30 transition-colors"
          data-testid="game-complete-new-game">NEW PATH</button>
      </div>
    </motion.div>
  </div>
);

// ─── Main Game Page ───
const GamePage = () => {
  const [screen, setScreen] = useState('loading');
  const [game, setGame] = useState(null);
  const [eraData, setEraData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transitionEra, setTransitionEra] = useState(null);
  const [diegoUnlocked, setDiegoUnlocked] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [mattAutoTrigger, setMattAutoTrigger] = useState(null);
  const [savedGamePreview, setSavedGamePreview] = useState(null);
  const [dariusTransition, setDariusTransition] = useState(false);
  const [policeTrap, setPoliceTrap] = useState(null);
  const [flightOverlay, setFlightOverlay] = useState(false);
  useEffect(() => {
    const h = (e) => setFlightOverlay(!!e.detail);
    window.addEventListener('kow-flight-overlay', h);
    return () => window.removeEventListener('kow-flight-overlay', h);
  }, []);
  const [policeRaid, setPoliceRaid] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const checkMattTrigger = useCallback(async (gameId, triggerType) => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/matt/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger: triggerType }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.visible) {
          setMattAutoTrigger(data);
        }
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    const savedId = localStorage.getItem('kow_game_id');

    // Handle Stripe return — auto-load saved game for payment polling
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    const subscription = searchParams.get('subscription');
    if (sessionId && (payment === 'success' || subscription === 'success')) {
      if (savedId) loadGame(savedId);
      if (payment === 'success') pollPaymentStatus(sessionId);
      if (subscription === 'success') pollSubscriptionStatus(sessionId);
      setSearchParams({});
      return;
    }
    if (payment === 'cancelled' || subscription === 'cancelled') {
      if (savedId) loadGame(savedId);
      setPaymentStatus({ type: 'cancelled', message: 'Payment cancelled.' });
      setSearchParams({});
      setTimeout(() => setPaymentStatus(null), 4000);
      return;
    }

    // Normal load — show continue/new choice if saved game exists
    if (savedId) {
      loadSavedGamePreview(savedId).catch(() => {
        // Saved game no longer exists, go to character select
        localStorage.removeItem('kow_game_id');
        setScreen('select');
      });
    } else {
      setScreen('select');
    }
    checkDiegoUnlock();
  }, []);

  // Auto-refresh era data every 30s for passive tick visibility
  useEffect(() => {
    if (screen !== 'playing' || !game?.id) return;
    const interval = setInterval(() => {
      refreshEraData(game.id);
    }, 30000);
    return () => clearInterval(interval);
  }, [screen, game?.id]);

  const checkDiegoUnlock = async () => {
    try {
      const res = await fetch(`${API}/api/game/unlock/diego`);
      if (res.ok) {
        const data = await res.json();
        setDiegoUnlocked(data.unlocked);
      }
    } catch (e) {}
  };

  const refreshEraData = async (gameId) => {
    try {
      const eraRes = await fetch(`${API}/api/game/${gameId}/era`);
      if (eraRes.ok) setEraData(await eraRes.json());
    } catch (e) {}
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 6;
    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'error', message: 'Payment status check timed out. Contact support.' });
      setTimeout(() => setPaymentStatus(null), 5000);
      return;
    }

    setPaymentStatus({ type: 'pending', message: 'Verifying payment...' });

    try {
      const res = await fetch(`${API}/api/payments/status/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setPaymentStatus({ type: 'success', message: `Purchase complete! ${data.package_id} applied.` });
          // Refresh game state to show applied effect
          const savedId = localStorage.getItem('kow_game_id');
          if (savedId) loadGame(savedId);
          setTimeout(() => setPaymentStatus(null), 4000);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus({ type: 'error', message: 'Payment session expired.' });
          setTimeout(() => setPaymentStatus(null), 4000);
          return;
        }
      }
      // Still pending, poll again
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const pollSubscriptionStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'error', message: 'Subscription verification timed out.' });
      setTimeout(() => setPaymentStatus(null), 5000);
      return;
    }
    setPaymentStatus({ type: 'pending', message: 'Verifying Inner Circle subscription...' });
    try {
      const res = await fetch(`${API}/api/payments/subscription/verify/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.active) {
          setPaymentStatus({ type: 'success', message: 'INNER CIRCLE activated. All perks are now live.' });
          const savedId = localStorage.getItem('kow_game_id');
          if (savedId) loadGame(savedId);
          setTimeout(() => setPaymentStatus(null), 5000);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus({ type: 'error', message: 'Subscription session expired.' });
          setTimeout(() => setPaymentStatus(null), 4000);
          return;
        }
      }
      setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2500);
    } catch {
      setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2500);
    }
  };

  const loadGame = async (gameId) => {
    try {
      const [gameRes, eraRes] = await Promise.all([
        fetch(`${API}/api/game/${gameId}`),
        fetch(`${API}/api/game/${gameId}/era`)
      ]);
      if (gameRes.ok && eraRes.ok) {
        const gameData = await gameRes.json();
        setGame(gameData);
        setEraData(await eraRes.json());
        setScreen('playing');

        // Commissioner Rodriguez police trap on game load
        if (gameData.police_trap?.triggered) {
          setPoliceTrap(gameData.police_trap);
        }

        // Auto-link email if game has none but we have one stored
        const storedEmail = localStorage.getItem('kow_email');
        if (!gameData.email && storedEmail && storedEmail.includes('@')) {
          try {
            await fetch(`${API}/api/game/${gameId}/link-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: storedEmail }),
            });
          } catch (e) { /* silent */ }
        }
      } else {
        if (gameRes.status === 404) localStorage.removeItem('kow_game_id');
        setScreen('select');
      }
    } catch (e) {
      setScreen('select');
    }
  };

  const loadSavedGamePreview = async (gameId) => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setSavedGamePreview(data);
        setScreen('continue_or_new');
      } else {
        if (res.status === 404) localStorage.removeItem('kow_game_id');
        setScreen('select');
      }
    } catch {
      setScreen('select');
    }
  };

  const startGame = async (path, username, faction, email) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/game/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, username: username || 'Anonymous', faction: faction || 'cmd', email: email || null })
      });
      const data = await res.json();
      localStorage.setItem('kow_game_id', data.id);
      setGame(data);
      const eraRes = await fetch(`${API}/api/game/${data.id}/era`);
      setEraData(await eraRes.json());
      setScreen('playing');
    } catch (e) {
      console.error('Failed to start game', e);
    }
    setLoading(false);
  };

  const handleAction = async (action, payload = {}) => {
    if (!game?.id || loading) return;
    setLoading(true);

    const prevEra = game.current_era;

    try {
      // Daily refill goes to payments endpoint
      let url = action === 'daily-refill'
        ? `${API}/api/payments/${game.id}/daily-refill`
        : `${API}/api/game/${game.id}/${action}`;
      let options = { method: 'POST', headers: { 'Content-Type': 'application/json' } };

      if (Object.keys(payload).length > 0) {
        options.body = JSON.stringify(payload);
      }

      const res = await fetch(url, options);
      if (res.ok) {
        const data = await res.json();

        // Show collect result feedback
        if (action === 'territory/collect' && data.collect_result) {
          const cr = data.collect_result;
          setGame(prev => prev ? ({ ...prev, narrator_message: `COLLECTED: +$${cr.cash?.toLocaleString()} | +${cr.xp}XP | +${cr.respect} Respect` }) : prev);
        }

        // Daily refill returns {success, energy} — reload full game state
        if (action === 'daily-refill') {
          const gameRes = await fetch(`${API}/api/game/${game.id}`);
          if (gameRes.ok) setGame(await gameRes.json());
          const eraRes = await fetch(`${API}/api/game/${game.id}/era`);
          if (eraRes.ok) setEraData(await eraRes.json());
          setLoading(false);
          return;
        }

        setGame(data);

        // Commissioner Rodriguez police trap check
        if (data.police_trap?.triggered) {
          setPoliceTrap(data.police_trap);
        }

        // Police raid check
        if (data.police_raid?.raided) {
          setPoliceRaid(data.police_raid);
        }

        const eraRes = await fetch(`${API}/api/game/${game.id}/era`);
        if (eraRes.ok) {
          setEraData(await eraRes.json());
        }

        if (data.current_era > prevEra) {
          const eraInfo = {
            1: { title: "THE FOUNDATION", subtitle: "East New York conquered." },
            2: { title: "THE RISE", subtitle: "Brooklyn is yours. CMD is born." },
            3: { title: "THE PEAK", subtitle: "The empire rises. Every borough. Every pipeline." },
            4: { title: "THE CONSEQUENCES", subtitle: "Twenty years of patience. The reckoning begins." },
          };
          setTransitionEra({ num: prevEra, ...eraInfo[prevEra] });
          // Don't auto-dismiss — let user click "ENTER NEXT ERA"
          // Matt trigger: new era
          setTimeout(() => checkMattTrigger(game.id, 'new_era'), 4500);
        }

        // Matt auto-triggers based on game state after action
        if (!transitionEra && data.current_era === prevEra) {
          if (data.energy <= 0) {
            setTimeout(() => checkMattTrigger(game.id, 'energy_zero'), 800);
          } else if (action === 'job' && data.job_result && !data.job_result.success) {
            // Check fail streak (3+ consecutive fails)
            setTimeout(() => checkMattTrigger(game.id, 'job_fail_streak'), 800);
          } else if (action === 'territory') {
            const prevOwned = game.territories_owned?.length || 0;
            if (prevOwned === 0 && data.territories_owned?.length > 0) {
              setTimeout(() => checkMattTrigger(game.id, 'first_territory'), 800);
            } else {
              setTimeout(() => checkMattTrigger(game.id, 'random'), 1000);
            }
          } else if (action === 'job' && data.job_result?.success) {
            // Level up check
            const prevLevel = game.level;
            if (data.level > prevLevel) {
              setTimeout(() => checkMattTrigger(game.id, 'level_up'), 800);
            } else {
              setTimeout(() => checkMattTrigger(game.id, 'random'), 1500);
            }
          }
        }
      } else {
        const err = await res.json().catch(() => ({ detail: `API error: ${res.status} ${res.statusText}` }));
        console.error('Action failed:', action, err);
        setGame(prev => ({ ...prev, narrator_message: `ERROR: ${err.detail || `Action failed (${res.status})`}` }));
      }

      // Delayed refresh to pick up AI narrator line (fires in background on server)
      setTimeout(async () => {
        try {
          const id = localStorage.getItem('kow_game_id');
          if (id) {
            const r = await fetch(`${API}/api/game/${id}`);
            if (r.ok) {
              const freshState = await r.json();
              if (freshState.narrator_message && freshState.narrator_message !== game?.narrator_message) {
                setGame(prev => prev ? ({ ...prev, narrator_message: freshState.narrator_message }) : prev);
              }
            }
          }
        } catch (_) {}
      }, 4000);
    } catch (e) {
      console.error('Action failed', e);
    }
    setLoading(false);
  };

  return (
    <div data-testid="game-page">
      {/* Payment Status Banner */}
      <AnimatePresence>
        {paymentStatus && (
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}
            className={`fixed top-0 left-0 right-0 z-[60] py-3 px-4 text-center font-['Outfit'] text-sm tracking-wider ${
              paymentStatus.type === 'success' ? 'bg-[#4ADE80]/20 text-[#4ADE80] border-b border-[#4ADE80]/30' :
              paymentStatus.type === 'pending' ? 'bg-[#C9A84C]/20 text-[#C9A84C] border-b border-[#C9A84C]/30' :
              paymentStatus.type === 'cancelled' ? 'bg-[#E8E0D0]/10 text-[#E8E0D0]/60 border-b border-[#E8E0D0]/10' :
              'bg-[#8B1A1A]/20 text-[#8B1A1A] border-b border-[#8B1A1A]/30'
            }`} data-testid="payment-status-banner">
            {paymentStatus.message}
          </motion.div>
        )}
      </AnimatePresence>

      {transitionEra && (
        <EraTransition eraNum={transitionEra.num} title={transitionEra.title} subtitle={transitionEra.subtitle}
          onContinue={() => setTransitionEra(null)} />
      )}

      {screen === 'loading' && (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <div className="film-grain" />
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs tracking-widest animate-pulse">LOADING...</p>
        </div>
      )}

      {screen === 'continue_or_new' && savedGamePreview && (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
          <div className="film-grain" />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full">
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] text-center mb-2">
              KILLING OVER WATER
            </h1>
            <p className="font-['Outfit'] text-[#E8E0D0]/40 text-center italic mb-8">
              "The water remembers every move you make."
            </p>

            {/* Saved game card */}
            <div className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-['Outfit'] text-[#C9A84C]/40 text-[9px] tracking-widest">SAVED GAME</span>
                <span className={`font-['Outfit'] text-[10px] tracking-widest ${savedGamePreview.game_complete ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/40'}`}>
                  {savedGamePreview.game_complete ? 'COMPLETE' : `ERA ${savedGamePreview.current_era}`}
                </span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0] mb-1">
                {savedGamePreview.username || 'Anonymous'}
              </h3>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs mb-3">
                {savedGamePreview.path_name || savedGamePreview.path?.toUpperCase() || 'Unknown Path'}
                <span className="text-[#E8E0D0]/20 mx-2">|</span>
                {savedGamePreview.faction?.toUpperCase() || 'CMD'}
              </p>
              <div className="flex items-center gap-4 text-[11px] font-['Outfit']">
                <span className="text-[#C9A84C]/60">Lv.{savedGamePreview.level || 1} {savedGamePreview.level_title || ''}</span>
                <span className="text-[#4ADE80]/50">${(savedGamePreview.cash || 0).toLocaleString()}</span>
                <span className="text-[#9F7AEA]/50">{savedGamePreview.water_crystals || 0} Crystals</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              <button onClick={() => { loadGame(savedGamePreview.id); }}
                className="w-full px-6 py-3.5 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-sm tracking-widest hover:bg-[#C9A84C]/20 hover:border-[#C9A84C]/60 transition-all"
                data-testid="continue-saved-game-btn">
                CONTINUE GAME
              </button>
              <button onClick={() => {
                localStorage.removeItem('kow_game_id');
                setSavedGamePreview(null);
                setScreen('select');
              }}
                className="w-full px-6 py-3 bg-[#111820] border border-[#1A2535] rounded-lg text-[#E8E0D0]/40 font-['Outfit'] text-sm tracking-widest hover:text-[#E8E0D0]/70 hover:border-[#E8E0D0]/20 transition-all"
                data-testid="start-new-game-btn">
                START NEW GAME
              </button>
            </div>

            <p className="text-center mt-6">
              <a href="/leaderboard" className="font-['Outfit'] text-[#E8E0D0]/20 text-[10px] tracking-widest hover:text-[#C9A84C] transition-colors" data-testid="continue-leaderboard-link">VIEW THE LEDGER</a>
            </p>
          </motion.div>
        </div>
      )}

      {screen === 'select' && <CharacterSelect onStart={startGame} onResume={loadGame} />}
      {screen === 'playing' && game?.game_complete && (
        <GameComplete game={game}
          onContinuePlaying={async () => {
            try {
              const res = await fetch(`${API}/api/game/${game.id}/continue`, { method: 'POST' });
              const data = await res.json();
              if (data.diego_evolved) {
                // Trigger cinematic transition for Diego → Darius
                setDariusTransition(true);
              } else {
                setGame({ ...game, game_complete: false, continue_playing: true });
                const id = localStorage.getItem('kow_game_id');
                if (id) loadGame(id);
              }
            } catch (e) { console.error(e); }
          }}
          onNewGame={() => { localStorage.removeItem('kow_game_id'); setGame(null); setEraData(null); setScreen('select'); }} />
      )}
      {screen === 'playing' && !game?.game_complete && (
        <GameDashboard game={game} eraData={eraData} onAction={handleAction} loading={loading}
          onNewGame={() => { localStorage.removeItem('kow_game_id'); setGame(null); setEraData(null); setScreen('select'); }}
          onRefreshGame={() => { const id = localStorage.getItem('kow_game_id'); if (id) loadGame(id); }} />
      )}

      {/* Matt auto-trigger overlay */}
      <AnimatePresence>
        {mattAutoTrigger && game?.id && (
          <BackpackMattStore
            gameId={game.id}
            onClose={() => setMattAutoTrigger(null)}
            onPurchase={() => { const id = localStorage.getItem('kow_game_id'); if (id) loadGame(id); }}
            playerCash={game?.cash}
            autoTriggerData={mattAutoTrigger}
          />
        )}
      </AnimatePresence>

      {/* Darius cinematic transition */}
      <AnimatePresence>
        {dariusTransition && (
          <DariusTransition onComplete={() => {
            setDariusTransition(false);
            const id = localStorage.getItem('kow_game_id');
            if (id) loadGame(id);
          }} />
        )}
      </AnimatePresence>

      {/* Commissioner Toribio Rodriguez — Police Trap */}
      {policeTrap && game?.id && !flightOverlay && (
        <PoliceTrapPopup
          trapData={policeTrap}
          gameId={game.id}
          playerCash={game.cash || 0}
          onResolved={(updatedState) => {
            setPoliceTrap(null);
            if (updatedState) setGame(updatedState);
          }}
        />
      )}

      {/* Police Raid Popup */}
      {policeRaid && (
        <PoliceRaidPopup
          raidData={policeRaid}
          onDismiss={() => {
            setPoliceRaid(null);
            const id = localStorage.getItem('kow_game_id');
            if (id) loadGame(id);
          }}
        />
      )}
    </div>
  );
};

export default GamePage;
